import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { Outlet } from "react-router-dom";
import GoalQuestions from "./GoalQuestions_new";
import { useQuiz } from "../contexts/QuizContext";
import { SyllabusContext } from "../contexts/SyllabusContext";
import { toast } from "react-toastify";
import {
  FaTimes,
  FaChevronDown,
  FaChevronRight,
  FaEye,
  FaUpload,
  FaTrash,
  FaFilter,
  FaFolderOpen,
} from "react-icons/fa";
import { generatePdfThumbnail } from "../utils/pdfThumbnail";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../styles/components/SamplePaper.css";
import notesFallback from "../data/notesFallback.json";
import SimpleThreeDotsLoader from "./SimpleThreeDotsLoader";
import RateLimitModal from "./RateLimitModal";
import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";

const mathJaxConfig = {
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
  },
};

const QUESTION_PROMPT =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore";

const MCQ_OPTIONS = [
  { value: "A", label: "A. Lorem ipsum" },
  { value: "B", label: "B. Dolor sit amet" },
  { value: "C", label: "C. Consectetur adipiscing elit" },
  { value: "D", label: "D. Sed do eiusmod tempor incididunt ut labore" },
];

const UPLOAD_TABS = [
  { id: "upload", label: "Upload New Notes", icon: FaUpload },
  { id: "library", label: "Select from My Uploads", icon: FaFolderOpen },
];

// Provided questions (mapped from user's JSON)
const PROVIDED_QUESTIONS_SOURCE = [
  {
    question:
      "For the polynomial $P(x) = 8x^4 - 2x^2 + 6x - 5$, what is the sum of its four zeroes when you multiply them in combinations of three?",
    options: [
      { label: "(A)", value: "0" },
      { label: "(B)", value: "-1/4" },
      { label: "(C)", value: "-3/4" },
      { label: "(D)", value: "-5/8" },
    ],
    correct_option: "(C)",
    explanation:
      "The formula for the sum of zeroes taken three at a time in a fourth-degree polynomial ($ax^4 + bx^3 + cx^2 + dx + e$) is **minus d divided by a** ($-d/a$).\nIn this polynomial, the coefficients are: $a=8$ (from $x^4$), $d=6$ (from $x$).\nTherefore, the sum is $-6/8$, which simplifies to **-3/4**.",
  },
  {
    question:
      "If the two solutions (zeroes) of the quadratic equation ax^2 + bx + c = 0 (where c is not 0) are identical, what must be true about the signs of coefficients a and c?",
    options: [
      {
        label: "(A)",
        value: "c and a must have opposite signs (one positive, one negative)",
      },
      { label: "(B)", value: "c and b must have opposite signs" },
      {
        label: "(C)",
        value:
          "c and a must have the same sign (both positive or both negative)",
      },
      { label: "(D)", value: "c and b must have the same sign" },
    ],
    correct_option: "(C)",
    explanation:
      "For a quadratic polynomial to have equal zeroes, the discriminant (b^2 - 4ac) must be equal to zero. This means b^2 = 4ac.\nSince b^2 must be zero or positive, 4ac must also be positive. The only way the product of $a$ and c$ ($ac$) can be positive is if **$a$ and $c$ have the same sign**.",
  },
  {
    question:
      "The graph of the quadratic polynomial $y = ax^2 + bx + c$ opens **upwards**, crosses the Y-axis **above the origin**, and its lowest point (vertex) is in the **top-right quarter** of the graph. Which set of signs is correct for the coefficients $a, b,$ and $c$?",
    options: [
      { label: "(A)", value: "a is negative, b is positive, c is positive" },
      { label: "(B)", value: "a is positive, b is negative, c is positive" },
      { label: "(C)", value: "a is positive, b is positive, c is negative" },
      { label: "(D)", value: "a is negative, b is negative, c is negative" },
    ],
    correct_option: "(B)",
    explanation:
      "1.  **Sign of a:** Opens **upwards** means **$a$ is positive** ($a > 0$).\n2.  **Sign of c:** Crosses Y-axis **above the origin** means **$c$ is positive** ($c > 0$).\n3.  **Sign of b:** The x-coordinate of the vertex is calculated as $-b/(2a)$. Since the vertex is in the **top-right quarter**, its x-coordinate is positive. With $a$ being positive, $-b$ must also be positive for the whole expression to be positive. If $-b$ is positive, then **$b$ is negative** ($b < 0$).",
  },
  {
    question:
      "If the polynomial $x^2 + kx + k$ has two distinct solutions, and it is known that $k$ is a positive number, what must be true about the sign of its two solutions?",
    options: [
      { label: "(A)", value: "Both solutions are positive" },
      { label: "(B)", value: "Both solutions are negative" },
      { label: "(C)", value: "One solution is positive and one is negative" },
      { label: "(D)", value: "One solution is zero" },
    ],
    correct_option: "(B)",
    explanation:
      "Let the two solutions be $x_1$ and $x_2$.\n1.  **Product of solutions** ($x_1 \\cdot x_2$) is $c/a$, which is $k/1 = k$. Since $k$ is positive, the product is **positive**. This means $x_1$ and $x_2$ must have the same sign.\n2.  **Sum of solutions** ($x_1 + x_2$) is $-b/a$, which is $-k/1 = -k$. Since $k$ is positive, the sum is **negative**.\nThe only way two numbers can have a positive product and a negative sum is if **both numbers are negative**.",
  },
  {
    question:
      "P(x) is a third-degree polynomial (a cubic) with a leading coefficient of 1. If we know that P(1)=1, P(2)=2, and P(3)=3, what is the value of P(4)?",
    options: [
      { label: "(A)", value: "4" },
      { label: "(B)", value: "12" },
      { label: "(C)", value: "10" },
      { label: "(D)", value: "13" },
    ],
    correct_option: "(C)",
    explanation:
      "The relationship P(x) = x holds true for $x=1, 2,$ and $3$. This suggests we can define a new polynomial $Q(x) = P(x) - x$.\nSince $P(1)=1$, $Q(1) = 1-1 = 0$. Similarly, $Q(2)=0$ and $Q(3)=0$. The zeroes of $Q(x)$ are 1, 2, and 3.\nSince $P(x)$ has a leading coefficient of 1, $Q(x)$ must be $Q(x) = 1 \\cdot (x-1)(x-2)(x-3)$.\nWe can rewrite $P(x)$ as: $P(x) = Q(x) + x = (x-1)(x-2)(x-3) + x$.\nTo find P(4): P(4) = (4-1)(4-2)(4-3) + 4\nP(4) = (3)(2)(1) + 4\nP(4) = $6 + 4 = \\mathbf{10}$.",
  },
];

// Map provided questions to GoalQuestions schema
const buildProvidedQuestions = (limit) => {
  const sanitize = (text) =>
    typeof text === "string" ? text.replace(/[\$\*]/g, "") : text;
  const items = PROVIDED_QUESTIONS_SOURCE.slice(0, limit);
  return items.map((q) => {
    const optionValues = q.options.map((o) => sanitize(o.value));
    const correctIndex = q.options.findIndex(
      (o) => o.label === q.correct_option
    );
    return {
      questionText: sanitize(q.question),
      options: optionValues,
      correctAnswer: Math.max(0, correctIndex),
      explanation: sanitize(q.explanation) || "",
    };
  });
};

// Utility functions for file type detection and handling
const getFileType = (filePath) => {
  const extension = filePath.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
    return "image";
  } else if (extension === "pdf") {
    return "pdf";
  }
  return "unknown";
};

const buildAbsoluteUrl = (path, baseUrl) => {
  if (!path) return "";
  if (/^(https?:|blob:|data:)/i.test(path)) {
    return path;
  }
  if (!baseUrl) return path;

  const trimmedBase = baseUrl.replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${trimmedBase}${normalizedPath}`;
};

const safeNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const LatexText = ({
  text,
  inline = false,
  component: Component = inline ? "span" : "div",
  className,
}) => (
  <Component className={className}>
    <MathJax inline={inline} dynamic>
      {text !== undefined && text !== null ? String(text) : ""}
    </MathJax>
  </Component>
);
const formatDateTime = (dateString) => {
  if (!dateString) return "Date not available";
  try {
    // Parse the date string (API already includes timezone info)
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      // Show time if within 24 hours (local timezone)
      return date.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    } else if (diffInHours < 24 * 7) {
      // Show date and day with time if within a week (local timezone)
      const dateStr = date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      const dayTimeStr = date.toLocaleDateString(undefined, {
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      return `${dateStr}, ${dayTimeStr}`;
    } else {
      // Show full date and time for older notes (local timezone)
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    }
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Date not available";
  }
};
const transformNotesResponse = (data, baseUrl, options = {}) => {
  const { fileBaseUrl, mediaPrefix = "/media", apiOrigin } = options;
  const resolvedFileBase = fileBaseUrl || baseUrl;

  const entries = Array.isArray(data)
    ? data
    : data && typeof data === "object"
    ? Array.isArray(data.attempts)
      ? data.attempts
      : Array.isArray(data.results) &&
        !Array.isArray(data.notes_data) &&
        !data.notes_extracted_content
      ? data.results
      : [data]
    : [];

  return entries
    .map((item, index) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const attemptId =
        item?.attempt_id || item?.id || item?.note_id || `note-${index + 1}`;
      const extracted = item.notes_extracted_content || {};
      const filesSource = Array.isArray(item.notes_data)
        ? [...item.notes_data]
        : [];
      filesSource.sort((a, b) => {
        const aIndex =
          typeof a === "object" &&
          a !== null &&
          typeof a.image_index === "number"
            ? a.image_index
            : Number.MAX_SAFE_INTEGER;
        const bIndex =
          typeof b === "object" &&
          b !== null &&
          typeof b.image_index === "number"
            ? b.image_index
            : Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });
      const files = filesSource
        .map((fileEntry, fileIndex) => {
          if (!fileEntry) return null;
          let rawPath = "";
          let displayName = "";

          if (typeof fileEntry === "string") {
            rawPath = fileEntry;
            displayName =
              fileEntry.split(/[\\/]/).pop() || `File ${fileIndex + 1}`;
          } else if (typeof fileEntry === "object") {
            rawPath =
              fileEntry.path ||
              fileEntry.url ||
              fileEntry.location ||
              fileEntry.fileUrl ||
              "";
            displayName =
              fileEntry.name ||
              fileEntry.filename ||
              rawPath.split(/[\\/]/).pop() ||
              `File ${fileIndex + 1}`;
            if (!rawPath && typeof fileEntry.filename === "string") {
              const sanitizedFileName = fileEntry.filename.replace(/^\/+/, "");
              rawPath = `${mediaPrefix.replace(
                /\/+$/,
                ""
              )}/${sanitizedFileName}`;
            }
          }

          if (!rawPath && displayName) {
            rawPath = displayName;
          }

          const inferMimeFromBase64 = (b64) => {
            const head = b64.slice(0, 24);
            if (head.startsWith("JVBER")) return "application/pdf";
            if (head.includes("iVBORw0KGgo") || head.includes("iVBOR"))
              return "image/png";
            if (head.includes("/9j/")) return "image/jpeg";
            if (head.includes("R0lGOD")) return "image/gif";
            if (head.includes("UklGR")) return "image/webp";
            return "application/octet-stream";
          };

          if (
            typeof fileEntry?.base64_data === "string" &&
            fileEntry.base64_data.trim()
          ) {
            const rawB64 = fileEntry.base64_data.replace(
              /^data:.*;base64,/,
              ""
            );
            const sanitizedB64 = rawB64.replace(/\s+/g, "").trim();

            const contentType =
              typeof fileEntry.content_type === "string" &&
              fileEntry.content_type.trim().length
                ? fileEntry.content_type.trim().toLowerCase()
                : "";

            const inferredMime = inferMimeFromBase64(sanitizedB64);
            const mime = contentType || inferredMime;

            const isPdf = mime.includes("pdf");
            const computedType = isPdf
              ? "pdf"
              : mime.startsWith("image/")
              ? "image"
              : "unknown";
            const dataUrl = `data:${mime};base64,${sanitizedB64}`;

            return {
              ...fileEntry,
              name:
                fileEntry.name ||
                fileEntry.filename ||
                displayName ||
                (isPdf ? "document.pdf" : "file"),
              path: dataUrl,
              type: computedType,
              thumbnail: isPdf ? null : dataUrl,
              originalPath: rawPath || "",
              alternatePaths: [],
              base64_data: sanitizedB64,
              content_type: mime,
            };
          }

          if (!rawPath) return null;

          const absolutePath = buildAbsoluteUrl(rawPath, resolvedFileBase);
          const apiAbsolutePath =
            apiOrigin && apiOrigin !== resolvedFileBase
              ? buildAbsoluteUrl(rawPath, apiOrigin)
              : "";
          const baseAbsolutePath =
            baseUrl && baseUrl !== resolvedFileBase
              ? buildAbsoluteUrl(rawPath, baseUrl)
              : "";
          const pathCandidates = [
            absolutePath,
            apiAbsolutePath,
            baseAbsolutePath,
            rawPath,
          ]
            .filter((candidate) => typeof candidate === "string" && candidate)
            .filter(
              (candidate, candidateIndex, self) =>
                self.indexOf(candidate) === candidateIndex
            );
          const finalPath = pathCandidates[0] || "";
          const alternatePaths = pathCandidates.slice(1);

          let type = getFileType(rawPath || displayName || "");
          if (
            type === "unknown" &&
            typeof fileEntry.content_type === "string"
          ) {
            const loweredContentType = fileEntry.content_type.toLowerCase();
            if (loweredContentType.includes("pdf")) {
              type = "pdf";
            } else if (
              loweredContentType.includes("image") ||
              loweredContentType.includes("jpg") ||
              loweredContentType.includes("png")
            ) {
              type = "image";
            }
          }

          return {
            ...fileEntry,
            path: finalPath,
            type,
            name: displayName,
            thumbnail: type === "image" ? finalPath : null,
            originalPath: rawPath,
            alternatePaths,
          };
        })
        .filter(Boolean);

      const attempts = Array.isArray(item.attempts) ? item.attempts : [];
      const shouldCreateSyntheticAttempt =
        (!attempts || attempts.length === 0) &&
        (item?.attempt_id ||
          item?.marks_received !== undefined ||
          item?.total_score !== undefined ||
          item?.overallMarks !== undefined ||
          item?.totalMarks !== undefined);

      const attemptSource = shouldCreateSyntheticAttempt ? [item] : attempts;

      const transformedAttempts = attemptSource.map((attempt, attemptIndex) => {
        const normalizedWrongAnswers = Array.isArray(attempt?.wrong_answers)
          ? attempt.wrong_answers.map((wrong) => ({ ...wrong }))
          : [];
        const normalizedQuestions = Array.isArray(attempt?.questions)
          ? attempt.questions.map((question, questionIndex) => ({
              ...question,
              id:
                question?.id ||
                `question-${index}-${attemptIndex}-${questionIndex}`,
              marks: safeNumber(question?.marks),
              totalMarks: safeNumber(question?.totalMarks),
              stepBreakdown: Array.isArray(question?.stepBreakdown)
                ? question.stepBreakdown.map((step, stepIndex) => ({
                    ...step,
                    step: safeNumber(step?.step) ?? stepIndex + 1,
                    marks: safeNumber(step?.marks),
                    maxMarks: safeNumber(step?.maxMarks),
                  }))
                : [],
            }))
          : [];

        return {
          ...attempt,
          attempt_id:
            attempt?.attempt_id ||
            attempt?.id ||
            `${attemptId}-attempt-${attemptIndex}`,
          marks_received: safeNumber(attempt?.marks_received),
          total_score: safeNumber(attempt?.total_score),
          overallMarks: safeNumber(attempt?.overallMarks),
          totalMarks: safeNumber(attempt?.totalMarks),
          wrong_answers: normalizedWrongAnswers,
          questions: normalizedQuestions,
        };
      });

      const rawResults = Array.isArray(item.results) ? item.results : [];
      const transformedResults = rawResults.map((result, resultIndex) => ({
        ...result,
        attempt_id:
          result?.attempt_id || result?.id || `result-${index}-${resultIndex}`,
        overallMarks: safeNumber(result?.overallMarks),
        totalMarks: safeNumber(result?.totalMarks),
        date: result?.date || result?.attempted_date || null,
        questions: Array.isArray(result?.questions)
          ? result.questions.map((question, questionIndex) => ({
              ...question,
              id: question?.id || `question-${resultIndex}-${questionIndex}`,
              marks: safeNumber(question?.marks),
              totalMarks: safeNumber(question?.totalMarks),
              stepBreakdown: Array.isArray(question?.stepBreakdown)
                ? question.stepBreakdown.map((step, stepIndex) => ({
                    ...step,
                    step:
                      typeof step?.step === "number"
                        ? step.step
                        : parseInt(step?.step, 10) || stepIndex + 1,
                    marks: safeNumber(step?.marks),
                    maxMarks: safeNumber(step?.maxMarks),
                  }))
                : [],
            }))
          : [],
      }));

      const normalizedSubject =
        (typeof extracted.subject === "string" && extracted.subject.trim()) ||
        (typeof item.subject === "string" && item.subject.trim()) ||
        "General Subject";
      const normalizedTopic =
        (typeof extracted.topic === "string" && extracted.topic.trim()) ||
        (typeof item.chapter === "string" && item.chapter.trim()) ||
        "General Topic";
      const normalizedSummary =
        typeof extracted.content_summary === "string"
          ? extracted.content_summary
          : typeof item.content_summary === "string"
          ? item.content_summary
          : "";

      return {
        ...item,
        id: item?.id || attemptId,
        note_id: item?.note_id || attemptId,
        subject: normalizedSubject,
        chapter: normalizedTopic,
        content_summary: normalizedSummary,
        notes_data: files,
        notes_extracted_content: {
          subject: normalizedSubject,
          topic: normalizedTopic,
          content_summary: normalizedSummary,
        },
        attempts: transformedAttempts,
        results: transformedResults,
      };
    })
    .filter(Boolean);
};

const createInitialNotes = () => transformNotesResponse(notesFallback);

const formatFileSize = (size) => {
  if (size === null || size === undefined) {
    return "";
  }

  const normalizedSize =
    typeof size === "number"
      ? size
      : typeof size === "string"
      ? Number(size.trim())
      : Number(size);

  if (!Number.isFinite(normalizedSize)) {
    return typeof size === "string" ? size : "";
  }

  const kilobytes = normalizedSize / 1024;
  const fractionDigits = kilobytes >= 10 ? 0 : 1;

  return `${kilobytes.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} KB`;
};

const getFileCreatedAt = (file) => {
  if (!file) {
    return null;
  }

  if (file.createdAt) {
    return file.createdAt;
  }
  if (file.createdAT) {
    return file.createdAT;
  }
  if (file.created_at) {
    return file.created_at;
  }

  if (file.uploadDate && file.uploadTime) {
    return `${file.uploadDate} ${file.uploadTime}`;
  }

  if (file.uploadDate) {
    return file.uploadDate;
  }

  return null;
};

const parseCreatedAt = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const dateFromNumber = new Date(value);
    return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber;
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const candidates = [normalized];
    if (!normalized.includes("T") && normalized.includes(" ")) {
      candidates.push(normalized.replace(" ", "T"));
    }
    if (!normalized.endsWith("Z") && !normalized.match(/[+-]\d\d:?\d\d$/)) {
      candidates.push(`${normalized}Z`);
    }

    for (const candidate of candidates) {
      const parsed = new Date(candidate);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }

  return null;
};

const formatUploadDate = (file) => {
  const date = parseCreatedAt(getFileCreatedAt(file));

  if (!date) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const formatUploadTime = (file) => {
  const date = parseCreatedAt(getFileCreatedAt(file));

  if (!date) {
    return "";
  }

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};

const truncateFileName = (filename, maxLength = 30) => {
  if (
    !filename ||
    typeof filename !== "string" ||
    filename.length <= maxLength
  ) {
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex <= 0 || lastDotIndex === filename.length - 1) {
    return `${filename.slice(0, maxLength - 3)}...`;
  }

  const extension = filename.slice(lastDotIndex);
  const baseLength = maxLength - extension.length - 3;

  if (baseLength <= 0) {
    return `${filename.slice(0, maxLength - 3)}...`;
  }

  const baseName = filename.slice(0, baseLength);
  return `${baseName}...${extension}`;
};

const getFileExtensionLabel = (file) => {
  const name =
    file?.filename || file?.fileName || file?.name || file?.originalName || "";
  if (!name) {
    return "FILE";
  }

  const lastDotIndex = name.lastIndexOf(".");
  if (lastDotIndex < 0 || lastDotIndex === name.length - 1) {
    return name.slice(0, 3).toUpperCase();
  }

  return name.slice(lastDotIndex + 1).toUpperCase();
};

export const NotesPractice = () => {
  const [selectedQuestions, setSelectedQuestions] = useState(5);
  const [questionType, setQuestionType] = useState("MCQ");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedFileThumbnails, setUploadedFileThumbnails] = useState([]);
  const [librarySelectedFiles, setLibrarySelectedFiles] = useState([]);
  const [librarySelectedThumbnails, setLibrarySelectedThumbnails] = useState(
    []
  );
  const [oldNotes, setOldNotes] = useState(() => []);
  const [isLoadingNotes, setIsLoadingNotes] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showFileSelectionModal, setShowFileSelectionModal] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewImageAlt, setPreviewImageAlt] = useState("");
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [expandedQuizzes, setExpandedQuizzes] = useState(new Set());
  const [isDragOver, setIsDragOver] = useState(false);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const [activeUploadTab, setActiveUploadTab] = useState("upload");
  const [isDropZoneHovered, setIsDropZoneHovered] = useState(false);
  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [rateLimitServerMessage, setRateLimitServerMessage] = useState("");
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);
  const thumbnailsRef = useRef([]);
  const libraryThumbnailsRef = useRef([]);
  const { saveQuizState, resetQuizState } = useQuiz();
  const syllabusContext = useContext(SyllabusContext);
  const { subjects, getChaptersForSubject: getChaptersFromContext } =
    syllabusContext || {};
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const isMountedRef = useRef(true);
  const previousModalOpenRef = useRef(isModalOpen);
  const latestFetchRequestRef = useRef(0);
  const showRateLimitModal = useCallback((message) => {
    setRateLimitServerMessage(message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE);
    setIsRateLimitModalOpen(true);
  }, []);
  const handleRateLimitModalClose = useCallback(() => {
    setIsRateLimitModalOpen(false);
    setRateLimitServerMessage("");
  }, []);
  const deriveRateLimitMessageFromError = useCallback((error) => {
    if (!error) {
      return null;
    }
    if (error?.rateLimit && typeof error.message === "string") {
      return error.message;
    }
    if (error?.isRateLimit && typeof error.message === "string") {
      return error.message;
    }
    const rawMessage =
      typeof error === "string"
        ? error
        : typeof error?.message === "string"
        ? error.message
        : "";
    if (!rawMessage) {
      return null;
    }
    const normalized = rawMessage.toLowerCase();
    if (
      normalized.includes("rate limit") ||
      normalized.includes("rate_limit") ||
      normalized.includes("rate-limit") ||
      normalized.includes("rate_limit_exceeded") ||
      normalized.includes("429")
    ) {
      return rawMessage;
    }
    return null;
  }, []);

  const [isLoadingSelectedNote, setIsLoadingSelectedNote] = useState(false);
  const noteFetchControllerRef = useRef(null);
  const base64ObjectUrlCacheRef = useRef(new Map());
  const releaseBase64ObjectUrls = useCallback(() => {
    if (!base64ObjectUrlCacheRef.current.size) {
      return;
    }

    const canRevoke =
      typeof window !== "undefined" &&
      window.URL &&
      typeof window.URL.revokeObjectURL === "function";

    base64ObjectUrlCacheRef.current.forEach((cachedUrl) => {
      if (
        canRevoke &&
        typeof cachedUrl === "string" &&
        cachedUrl.startsWith("blob:")
      ) {
        try {
          window.URL.revokeObjectURL(cachedUrl);
        } catch (error) {
          console.warn("Failed to revoke object URL:", error);
        }
      }
    });

    base64ObjectUrlCacheRef.current.clear();
  }, []);

  useEffect(() => {
    thumbnailsRef.current = uploadedFileThumbnails;
  }, [uploadedFileThumbnails]);

  useEffect(() => {
    libraryThumbnailsRef.current = librarySelectedThumbnails;
  }, [librarySelectedThumbnails]);

  useEffect(() => {
    return () => {
      releaseBase64ObjectUrls();
    };
  }, [releaseBase64ObjectUrls]);

  useEffect(() => {
    if (!showNotesModal) {
      releaseBase64ObjectUrls();
    }
  }, [showNotesModal, releaseBase64ObjectUrls]);

  useEffect(() => {
    return () => {
      thumbnailsRef.current.forEach((thumb) => {
        if (typeof thumb === "string" && thumb.startsWith("blob:")) {
          URL.revokeObjectURL(thumb);
        }
      });
    };
  }, []);

  useEffect(() => {
    return () => {
      libraryThumbnailsRef.current.forEach((thumb) => {
        if (typeof thumb === "string" && thumb.startsWith("blob:")) {
          URL.revokeObjectURL(thumb);
        }
      });
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!showNotesModal) {
      if (noteFetchControllerRef.current) {
        noteFetchControllerRef.current.abort();
        noteFetchControllerRef.current = null;
      }
      setIsLoadingSelectedNote(false);
    }
  }, [showNotesModal]);

  useEffect(() => {
    return () => {
      if (noteFetchControllerRef.current) {
        noteFetchControllerRef.current.abort();
        noteFetchControllerRef.current = null;
      }
    };
  }, []);

  const fetchNotesHistory = useCallback(
    async ({ signal, showLoading = true } = {}) => {
      if (!isMountedRef.current) {
        return;
      }

      const requestId = latestFetchRequestRef.current + 1;
      latestFetchRequestRef.current = requestId;

      if (showLoading) {
        setIsLoadingNotes(true);
      }

      const apiUrl = `${baseUrl}/v2/quiz/attempts?quiz_type=notes`;

      try {
        const response = await fetch(apiUrl, {
          method: "GET",
          redirect: "follow",
          signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${await response.text()}`);
        }

        const payload = await response.json();

        if (
          signal?.aborted ||
          !isMountedRef.current ||
          requestId !== latestFetchRequestRef.current
        ) {
          return;
        }

        let apiOrigin = "";
        try {
          apiOrigin = new URL(apiUrl).origin;
        } catch {
          apiOrigin = "";
        }

        const normalized = transformNotesResponse(payload, baseUrl, {
          fileBaseUrl: baseUrl || apiOrigin,
          mediaPrefix: "/media",
        });

        setOldNotes(normalized);
      } catch (error) {
        if (signal?.aborted) {
          return;
        }

        console.error("Failed to load notes practise history:", error);

        if (
          !isMountedRef.current ||
          requestId !== latestFetchRequestRef.current
        ) {
          return;
        }

        toast.error("Unable to load your notes history right now.");
        setOldNotes([]);
      } finally {
        if (
          signal?.aborted ||
          !isMountedRef.current ||
          requestId !== latestFetchRequestRef.current
        ) {
          return;
        }

        setIsLoadingNotes(false);
      }
    },
    [baseUrl]
  );

  const fetchNotesData = useCallback(
    async (id, { signal, showLoading = true } = {}) => {
      if (!id) {
        throw new Error("Note id is required");
      }
      if (!baseUrl) {
        throw new Error("VITE_APP_BASE_URL is not configured.");
      }

      const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
      const apiUrl = `${trimmedBaseUrl}/v2/quiz/attempts-detail/${encodeURIComponent(
        id
      )}`;

      if (showLoading) {
        setIsLoadingSelectedNote(true);
      }

      try {
        const res = await fetch(apiUrl, { method: "GET", signal });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${await res.text()}`);
        }

        const payload = await res.json();
        if (signal?.aborted) {
          return null;
        }

        const attempt = payload?.attempt ?? payload;
        if (!attempt || typeof attempt !== "object") {
          return null;
        }

        const normalized = transformNotesResponse([attempt], baseUrl, {
          fileBaseUrl: baseUrl,
          mediaPrefix: "/media",
        });

        return Array.isArray(normalized) ? normalized[0] : normalized;
      } catch (err) {
        if (signal?.aborted) {
          return null;
        }
        console.error("Failed to fetch note details:", err);
        toast.error("Unable to load note details right now.");
        return null;
      } finally {
        if (!signal?.aborted) {
          setIsLoadingSelectedNote(false);
        }
      }
    },
    [baseUrl]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchNotesHistory({ signal: controller.signal });
    return () => {
      controller.abort();
    };
  }, [fetchNotesHistory]);

  useEffect(() => {
    let controller;
    if (previousModalOpenRef.current && !isModalOpen) {
      controller = new AbortController();
      fetchNotesHistory({ signal: controller.signal });
    }
    previousModalOpenRef.current = isModalOpen;

    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [isModalOpen, fetchNotesHistory]);
  const [userWorkbooks, setUserWorkbooks] = useState([]);
  const [isFetchingWorkbooks, setIsFetchingWorkbooks] = useState(false);
  const [uploadFilter, setUploadFilter] = useState({
    subject: "",
    chapter: "",
  });
  const [currMyUploadsPage, setCurrMyUploadsPage] = useState(1);
  const [totalWorkbookPages, setTotalWorkbookPages] = useState(1);
  const [totalWorkbookCount, setTotalWorkbookCount] = useState(0);
  const uploadFileLimit = 10;

  // Use syllabus context for chapters
  const getFilteredChapters = () => {
    if (!getChaptersFromContext) {
      return [];
    }
    return getChaptersFromContext(uploadFilter.subject);
  };

  const fetchUserUploadedWorkbooks = useCallback(
    async (page = 1) => {
      if (!baseUrl) {
        console.error("VITE_APP_BASE_URL is not configured.");
        return;
      }

      const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
      const params = new URLSearchParams({
        page: String(Math.max(page, 1)),
        limit: String(uploadFileLimit),
      });

      if (uploadFilter.subject) {
        params.append("subject", uploadFilter.subject);
      }
      if (uploadFilter.chapter) {
        params.append("chapter", uploadFilter.chapter);
      }

      setIsFetchingWorkbooks(true);
      try {
        const response = await fetch(
          `${trimmedBaseUrl}/v2/workbook/?${params.toString()}`,
          {
            method: "GET",
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Workbook fetch failed (${response.status}): ${errorText}`
          );
        }

        const result = await response.json();
        const items = Array.isArray(result?.items) ? result.items : [];

        const normalizeNumber = (value) => {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        };

        const totalCandidates = [
          result?.totalCount,
          result?.totalItems,
          result?.totalRecords,
          result?.count,
          result?.total,
        ]
          .map((candidate) => normalizeNumber(candidate))
          .filter((candidate) => candidate !== null && candidate >= 0);

        const totalRecords =
          totalCandidates.length > 0
            ? Math.max(0, Math.floor(totalCandidates[0]))
            : items.length;

        const pageCountFromRecords =
          totalRecords > 0
            ? Math.floor((totalRecords + uploadFileLimit - 1) / uploadFileLimit)
            : items.length > 0
            ? Math.floor((items.length + uploadFileLimit - 1) / uploadFileLimit)
            : 0;

        const totalPagesCandidate = normalizeNumber(result?.totalPages);
        const resolvedTotalPages =
          totalPagesCandidate && totalPagesCandidate > 0
            ? Math.floor(totalPagesCandidate)
            : Math.max(1, pageCountFromRecords || 1);

        const pageCandidate = normalizeNumber(result?.page);
        let resolvedPage =
          (pageCandidate && pageCandidate > 0
            ? Math.floor(pageCandidate)
            : normalizeNumber(page)) || 1;

        if (!Number.isFinite(resolvedPage) || resolvedPage <= 0) {
          resolvedPage = 1;
        }
        if (resolvedTotalPages > 0) {
          resolvedPage = Math.min(resolvedPage, resolvedTotalPages);
        }

        setUserWorkbooks(items);
        setTotalWorkbookCount(totalRecords > 0 ? totalRecords : items.length);
        setTotalWorkbookPages(resolvedTotalPages);
        setCurrMyUploadsPage(resolvedPage);
      } catch (error) {
        console.error("Error fetching user uploads:", error);
        toast.error("Failed to load your workbooks. Please try again.");
      } finally {
        setIsFetchingWorkbooks(false);
      }
    },
    [baseUrl, uploadFilter.chapter, uploadFilter.subject, uploadFileLimit]
  );

  useEffect(() => {
    if (!showFileSelectionModal) {
      return;
    }
    fetchUserUploadedWorkbooks(currMyUploadsPage);
  }, [showFileSelectionModal, currMyUploadsPage, fetchUserUploadedWorkbooks]);

  useEffect(() => {
    if (!showFileSelectionModal) {
      return;
    }
    setCurrMyUploadsPage((prev) => (prev === 1 ? prev : 1));
  }, [showFileSelectionModal, uploadFilter.chapter, uploadFilter.subject]);

  const getWorkbookRangeText = () => {
    if (!userWorkbooks.length) {
      return "No workbooks found";
    }

    const startIndex = (currMyUploadsPage - 1) * uploadFileLimit + 1;
    const totalRecords = totalWorkbookCount || userWorkbooks.length;
    const endIndex = Math.min(
      startIndex + userWorkbooks.length - 1,
      totalRecords
    );

    return `Showing ${startIndex} to ${endIndex} of ${totalRecords} files`;
  };

  const [fetchingPDF, setFetchingPDF] = useState(false);
  const [isImportingWorkbookId, setIsImportingWorkbookId] = useState(null);
  const generatePDFFromBase64 = async (base64Data) => {
    try {
      if (!base64Data || typeof base64Data !== "string") {
        throw new Error("base64Data must be a non-empty string");
      }

      const normalizedBase64 = base64Data.includes(",")
        ? base64Data.split(",")[1]
        : base64Data;

      const byteCharacters = atob(normalizedBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let index = 0; index < byteCharacters.length; index += 1) {
        byteNumbers[index] = byteCharacters.charCodeAt(index);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const objectUrl = URL.createObjectURL(blob);
      return { blob, objectUrl };
    } catch (error) {
      console.error("Error generating PDF from base64:", error);
    }
    return null;
  };

  const fetchPDFfromID = async (pdfID, workbookMeta = {}) => {
    try {
      const requestOptions = {
        method: "GET",
        redirect: "follow",
      };

      const response = await fetch(
        `${baseUrl}/v2/workbook/${pdfID}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch workbook ${pdfID}`);
      }

      const result = await response.json();
      const base64Payload =
        result?.base64_data ||
        result?.base64Data ||
        result?.data ||
        result?.file_data;

      if (!base64Payload) {
        throw new Error("Workbook did not return any PDF data");
      }

      const pdfAsset = await generatePDFFromBase64(base64Payload);

      if (!pdfAsset?.blob) {
        throw new Error("Failed to construct PDF blob from workbook data");
      }

      const inferredNameCandidate =
        workbookMeta?.filename ||
        workbookMeta?.fileName ||
        workbookMeta?.name ||
        workbookMeta?.originalName ||
        workbookMeta?.path ||
        `workbook-${pdfID}.pdf`;

      const trimmedName =
        typeof inferredNameCandidate === "string" &&
        inferredNameCandidate.trim().length > 0
          ? inferredNameCandidate.trim()
          : `workbook-${pdfID}`;

      const normalizedName = trimmedName.toLowerCase().endsWith(".pdf")
        ? trimmedName
        : `${trimmedName}.pdf`;

      const pdfFile = new File([pdfAsset.blob], normalizedName, {
        type: pdfAsset.blob.type || "application/pdf",
        lastModified: Date.now(),
      });

      await addLibraryFiles([pdfFile]);
      setActiveUploadTab("library");

      setPreviewPdf((prev) => {
        if (prev && prev.startsWith("blob:")) {
          URL.revokeObjectURL(prev);
        }
        return pdfAsset.objectUrl;
      });
      setPreviewImageAlt(normalizedName);
      setShowPdfPreview(true);
      setShowFileSelectionModal(false);
    } catch (error) {
      console.error("Error fetching PDF from ID:", error);
      toast.error("Failed to load the selected workbook. Please try again.");
      throw error;
    }
  };

  const openUploadedSheet = async (uploadedFile) => {
    console.log("Opening uploaded worksheet:", uploadedFile);
    setFetchingPDF(true);

    if (!uploadedFile?.id) {
      toast.error("This upload does not have an accessible file path.");
      setFetchingPDF(false);
      return;
    }
    try {
      setIsImportingWorkbookId(uploadedFile.id);
      await fetchPDFfromID(uploadedFile.id, uploadedFile);
    } catch (error) {
      console.error("Error opening uploaded worksheet:", error);
      toast.error("Failed to load uploaded worksheet");
    } finally {
      setFetchingPDF(false);
      setIsImportingWorkbookId(null);
    }
  };

  const normalizeQuestionType = (type) => {
    const normalized =
      typeof type === "string" ? type.trim().toLowerCase() : "mcq";
    switch (normalized) {
      case "short answer":
        return "short_answer";
      case "all":
        return "all";
      case "mcq":
        return "mcq";
      default:
        return normalized.replace(/[^a-z0-9]+/g, "_") || "mcq";
    }
  };

  const callNotesPractise = async (
    files,
    totalQuestions = 5,
    questionTypes
  ) => {
    if (!baseUrl) {
      throw new Error("VITE_APP_BASE_URL is not configured.");
    }

    const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
    const apiUrl = `${trimmedBaseUrl}/v2/quiz/notes-practice`;
    const fd = new FormData();

    if (Array.isArray(files)) {
      files.forEach((file, index) => {
        if (file) {
          const fileName =
            (typeof file === "object" && "name" in file && file.name) ||
            `note-${index + 1}`;
          fd.append("notes_images", file, fileName);
        }
      });
    }

    fd.append("total_questions", String(totalQuestions ?? 0));
    const typesPayload =
      Array.isArray(questionTypes) && questionTypes.length
        ? questionTypes
        : ["mcq"];
    fd.append("question_type", JSON.stringify(typesPayload));

    const res = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: fd,
    });

    const rateLimitMessage = await extractRateLimitMessageFromResponse(res);
    if (rateLimitMessage) {
      return {
        rateLimit: true,
        message: rateLimitMessage || RATE_LIMIT_FALLBACK_MESSAGE,
        status: res.status,
      };
    }

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    }

    return res.json();
  };

  const generateNewQuizVersionFromNotes = async (attemptId) => {
    if (!baseUrl) {
      throw new Error("VITE_APP_BASE_URL is not configured.");
    }

    if (!attemptId) {
      throw new Error("Attempt ID is required to regenerate questions.");
    }

    const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
    const apiUrl = `${trimmedBaseUrl}/v2/quiz/regenerate-questions/${attemptId}`;

    const res = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`HTTP ${res.status}: ${errorText}`);
    }

    return res.json();
  };

  // Process attempts data for chart
  const processAttemptsForChart = (attempts) => {
    if (!Array.isArray(attempts) || attempts.length === 0) return [];

    return attempts.map((attempt, index) => {
      const totalScoreValue =
        safeNumber(attempt?.total_score) ??
        safeNumber(attempt?.totalMarks) ??
        0;
      const marksReceivedValue =
        safeNumber(attempt?.marks_received) ??
        safeNumber(attempt?.overallMarks) ??
        0;
      const totalScore = totalScoreValue >= 0 ? totalScoreValue : 0;
      const marksReceived = marksReceivedValue >= 0 ? marksReceivedValue : 0;

      const dateSource = attempt?.attempted_date || attempt?.date;
      const attemptDate = dateSource ? new Date(dateSource) : null;
      const formattedDate = attemptDate
        ? attemptDate.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "Unknown date";

      const totalQuestions =
        typeof attempt?.total_questions === "number"
          ? attempt.total_questions
          : Number.isFinite(parseFloat(attempt?.total_questions))
          ? parseFloat(attempt?.total_questions)
          : null;

      // Use pre-calculated accuracy if available, otherwise calculate it
      const accuracy =
        attempt?.accuracy !== undefined
          ? attempt.accuracy
          : totalScore > 0
          ? Math.round((marksReceived / totalScore) * 100)
          : 0;

      return {
        attempt: `Attempt ${index + 1}`,
        accuracy: accuracy,
        score: marksReceived,
        maxMark: totalScore,
        totalQuestions: totalQuestions,
        correctAnswers: attempt?.correct_answers || 0,
        date: formattedDate,
      };
    });
  };

  const addNoteToHistory = (name) => {
    if (!name) {
      return;
    }
    setOldNotes((prev) => {
      const fallbackNote = transformNotesResponse(
        [
          {
            notes_data: [],
            notes_extracted_content: {
              subject: name,
              topic: "",
              content_summary: "",
            },
            attempts: [],
          },
        ],
        baseUrl
      );
      return [...(fallbackNote?.length ? fallbackNote : []), ...prev];
    });
  };

  // Handle note selection and modal opening
  const handleNoteClick = (note) => {
    // cancel previous in-flight details fetch
    if (noteFetchControllerRef.current) {
      noteFetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    noteFetchControllerRef.current = controller;

    // open modal with summary immediately
    setSelectedNote(note);
    setShowNotesModal(true);
    setIsContentExpanded(false);
    setExpandedQuizzes(new Set());
    setIsLoadingSelectedNote(true);

    const noteId = note?.id;
    if (!noteId) {
      setIsLoadingSelectedNote(false);
      toast.error("Missing note id to load details.");
      return;
    }

    (async () => {
      const full = await fetchNotesData(noteId, {
        signal: controller.signal,
        showLoading: false,
      });
      if (controller.signal.aborted) {
        return;
      }
      if (noteFetchControllerRef.current === controller) {
        noteFetchControllerRef.current = null;
      }
      if (full) {
        setSelectedNote(full);
      }
    })();
  };

  // Handle image preview
  const handleImagePreview = (imageSrc, imageAlt) => {
    setPreviewImage(imageSrc);
    setPreviewImageAlt(imageAlt);
    setShowImagePreview(true);
  };

  // Handle PDF preview
  const handlePdfPreview = (pdfSrc, pdfName) => {
    setPreviewPdf(pdfSrc);
    setPreviewImageAlt(pdfName);
    setShowPdfPreview(true);
  };

  // Handle quiz toggle
  const toggleQuiz = (quizId) => {
    setExpandedQuizzes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(quizId)) {
        newSet.delete(quizId);
      } else {
        newSet.add(quizId);
      }
      return newSet;
    });
  };

  // Handle new quiz button
  const handleNewQuiz = async () => {
    try {
      console.log("handleNewQuiz - selectedNote", selectedNote);
      // Get the attempt_id from the selected note
      const attemptId = selectedNote?.attempt_id;

      if (!attemptId) {
        throw new Error("No attempt ID found for this note.");
      }
      //show loader of generating quiz
      setIsGeneratingQuiz(true);
      const apiResponse = await generateNewQuizVersionFromNotes(attemptId);
      console.log(
        "apiResponse - handleNewQuiz - generateNewQuizVersionFromNotes"
      );
      console.log(apiResponse);

      const quizPayload =
        apiResponse?.result && typeof apiResponse.result === "object"
          ? apiResponse.result
          : apiResponse;

      const questionList = Array.isArray(quizPayload?.questions)
        ? quizPayload.questions
        : [];

      if (!questionList.length) {
        throw new Error("API did not return any questions.");
      }

      const totalQuestions =
        typeof quizPayload?.question_count === "number"
          ? quizPayload.question_count
          : questionList.length || selectedQuestions;

      const userAnswersSeed = Array.from(
        { length: totalQuestions },
        () => null
      );

      saveQuizState({
        topicId: "notes-practice",
        questions: quizPayload,
        goalDetails: { subject: "Notes Practice", current_level: 0 },
        currentQuestionIndex: 0,
        selectedOption: null,
        score: 0,
        showExplanation: false,
        quizCompleted: false,
        leveledUp: false,
        goalStatus: "",
        userAnswers: userAnswersSeed,
      });

      setSelectedAnswers(userAnswersSeed);
      setCurrentQuestionIndex(0);
      const computedTimer =
        typeof quizPayload?.timer === "number" && quizPayload.timer > 0
          ? quizPayload.timer
          : totalQuestions * 60;
      setRemainingTime(computedTimer);
      setIsModalOpen(true);
      clearAllUploadedFiles();
    } catch (error) {
      console.error("Failed to regenerate quiz from notes:", error);
      toast.error("Could not regenerate quiz. Please try again.");
    } finally {
      setIsGeneratingQuiz(false);
      setShowNotesModal(false);
    }
  };

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const input = e.target;
    const files = input && input.files ? Array.from(input.files) : [];
    if (files.length > 0) {
      try {
        await addFiles(files);
      } catch (error) {
        console.error("Failed to add files:", error);
        toast.error("Unable to add selected files. Please try again.");
      }
    }
    // Reset the input so selecting the same file again triggers onChange
    if (input) {
      input.value = "";
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      try {
        await addFiles(Array.from(files));
      } catch (error) {
        console.error("Failed to add dropped files:", error);
        toast.error("Unable to add dropped files. Please try again.");
      }
    }
  };

  // Check if file is PDF
  const isPdfFile = (file) => {
    if (typeof file === "string") {
      const lowered = file.toLowerCase();
      return (
        lowered.endsWith(".pdf") || lowered.startsWith("data:application/pdf")
      );
    }

    // Handle base64 data files
    if (file.base64_data && typeof file.base64_data === "string") {
      // Check explicit type information first (prioritize content_type from backend)
      if (file.content_type && file.content_type.includes("pdf")) {
        return true;
      }
      if (file.type && file.type.includes("pdf")) {
        return true;
      }
      if (file.mimeType && file.mimeType.includes("pdf")) {
        return true;
      }

      // Check base64 header for PDF signature
      if (
        file.base64_data.startsWith("JVBERi0") ||
        file.base64_data.startsWith("JVBER")
      ) {
        return true;
      }

      // Check filename if available
      if (file.name && file.name.toLowerCase().endsWith(".pdf")) {
        return true;
      }
      if (file.filename && file.filename.toLowerCase().endsWith(".pdf")) {
        return true;
      }

      return false;
    }

    const candidatePath =
      file?.path || file?.originalPath || file?.location || "";
    return (
      (file?.type && file.type.includes("pdf")) ||
      (file?.name && file.name.toLowerCase().endsWith(".pdf")) ||
      (typeof candidatePath === "string" &&
        candidatePath.toLowerCase().endsWith(".pdf"))
    );
  };

  // Remove uploaded file
  const revokeObjectUrlIfNeeded = (value) => {
    if (typeof value === "string" && value.startsWith("blob:")) {
      URL.revokeObjectURL(value);
    }
  };

  const resetPreviewDisplays = () => {
    if (
      previewImage &&
      typeof previewImage === "string" &&
      previewImage.startsWith("blob:")
    ) {
      URL.revokeObjectURL(previewImage);
    }

    if (
      previewPdf &&
      typeof previewPdf === "string" &&
      previewPdf.startsWith("blob:")
    ) {
      URL.revokeObjectURL(previewPdf);
    }

    setShowImagePreview(false);
    setPreviewImage(null);
    setPreviewImageAlt("");
    setShowPdfPreview(false);
    setPreviewPdf(null);
  };

  const clearAllUploadedFiles = () => {
    thumbnailsRef.current.forEach((thumb) => {
      revokeObjectUrlIfNeeded(thumb);
    });

    resetPreviewDisplays();
    setUploadedFiles([]);
    setUploadedFileThumbnails([]);
    clearLibraryFiles();
    setIsDragOver(false);

    if (fileInputRef?.current) {
      try {
        fileInputRef.current.value = "";
      } catch (_) {
        // no-op
      }
    }
  };

  const removeUploadedFile = (indexToRemove) => {
    if (typeof indexToRemove !== "number" || indexToRemove < 0) {
      return;
    }

    const thumbnailToRemove = uploadedFileThumbnails[indexToRemove];
    revokeObjectUrlIfNeeded(thumbnailToRemove);

    setUploadedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
    setUploadedFileThumbnails((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );

    resetPreviewDisplays();
    setIsDragOver(false);

    if (fileInputRef?.current) {
      try {
        fileInputRef.current.value = "";
      } catch (_) {
        // no-op
      }
    }
  };

  const removeLibraryFile = (indexToRemove) => {
    if (typeof indexToRemove !== "number" || indexToRemove < 0) {
      return;
    }

    const thumbnailToRemove = librarySelectedThumbnails[indexToRemove];
    revokeObjectUrlIfNeeded(thumbnailToRemove);

    setLibrarySelectedFiles((prev) =>
      prev.filter((_, idx) => idx !== indexToRemove)
    );
    setLibrarySelectedThumbnails((prev) =>
      prev.filter((_, idx) => idx !== indexToRemove)
    );

    resetPreviewDisplays();
  };

  const clearLibraryFiles = () => {
    libraryThumbnailsRef.current.forEach((thumb) => {
      revokeObjectUrlIfNeeded(thumb);
    });
    setLibrarySelectedFiles([]);
    setLibrarySelectedThumbnails([]);
  };

  // Handle uploaded file preview
  const handleUploadedFilePreview = (file) => {
    if (isPdfFile(file)) {
      setPreviewPdf(URL.createObjectURL(file));
      setPreviewImageAlt(file.name);
      setShowPdfPreview(true);
    } else {
      setPreviewImage(URL.createObjectURL(file));
      setPreviewImageAlt(file.name);
      setShowImagePreview(true);
    }
  };

  // Generate thumbnail for PDF/image files
  const generateThumbnailForFile = async (file) => {
    if (isPdfFile(file)) {
      try {
        const thumbnail = await generatePdfThumbnail(file, {
          width: 200,
          height: 200,
        });
        return thumbnail || "/images/answer_sheet_example.png";
      } catch (error) {
        console.error(`Failed to generate thumbnail for ${file.name}:`, error);
        return "/images/answer_sheet_example.png";
      }
    }
    return URL.createObjectURL(file);
  };

  const addFiles = async (files) => {
    if (!Array.isArray(files) || files.length === 0) return;

    const supportedFiles = [];
    const unsupportedFiles = [];

    files.forEach((file) => {
      if (!file) return;
      const type = file.type || "";
      const isSupported =
        type === "application/pdf" || type.startsWith("image/");
      if (isSupported) {
        supportedFiles.push(file);
      } else {
        unsupportedFiles.push(file.name || "Unsupported file");
      }
    });

    if (unsupportedFiles.length === files.length) {
      toast.error("Please upload PDF, JPG, or PNG files only.");
      return;
    }

    if (unsupportedFiles.length > 0) {
      toast.warn(
        `Some files were skipped due to unsupported formats: ${unsupportedFiles.join(
          ", "
        )}`
      );
    }

    if (supportedFiles.length === 0) return;

    const thumbnails = await Promise.all(
      supportedFiles.map((file) => generateThumbnailForFile(file))
    );

    setUploadedFiles((prev) => [...prev, ...supportedFiles]);
    setUploadedFileThumbnails((prev) => [...prev, ...thumbnails]);
  };

  const addLibraryFiles = async (files) => {
    if (!Array.isArray(files) || files.length === 0) return;

    try {
      const thumbnails = await Promise.all(
        files.map((file) => generateThumbnailForFile(file))
      );
      setLibrarySelectedFiles((prev) => [...prev, ...files]);
      setLibrarySelectedThumbnails((prev) => [...prev, ...thumbnails]);
    } catch (error) {
      console.error("Failed to add library files:", error);
      toast.error("Unable to add selected uploads. Please try again.");
    }
  };

  const closePracticeGame = () => {
    fetchNotesHistory();
    setIsModalOpen(false);
    setRemainingTime(0);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    resetQuizState();
  };

  const submitHandler = async () => {
    const usingUploadTab = activeUploadTab === "upload";
    const filesToUse = usingUploadTab ? uploadedFiles : librarySelectedFiles;

    if (filesToUse.length === 0) {
      toast.error(
        usingUploadTab
          ? "Please upload at least one note before creating a quiz."
          : "Please select at least one note from your uploads before creating a quiz."
      );
      return;
    }

    // setIsGeneratingQuiz(true);
    // const files = [...filesToUse];
    // const questionTypes = [normalizeQuestionType(questionType)];

    // try {
    //   const apiResponse = await callNotesPractise(
    //     files,
    //     selectedQuestions,
    //     questionTypes
    //   );

    //   if (apiResponse?.rateLimit) {
    //     const message = apiResponse.message || RATE_LIMIT_FALLBACK_MESSAGE;
    //     toast.warn(message);
    //     showRateLimitModal(message);
    //     setIsGeneratingQuiz(false);
    //     return;
    //   }

    //   console.log(apiResponse);
    //   const quizPayload =
    //     apiResponse?.result && typeof apiResponse.result === "object"
    //       ? apiResponse.result
    //       : apiResponse;

    //   const questionList = Array.isArray(quizPayload?.questions)
    //     ? quizPayload.questions
    //     : [];

    //   if (!questionList.length) {
    //     throw new Error("API did not return any questions.");
    //   }

    //   const totalQuestions =
    //     typeof quizPayload?.question_count === "number"
    //       ? quizPayload.question_count
    //       : questionList.length || selectedQuestions;

    //   const userAnswersSeed = Array.from(
    //     { length: totalQuestions },
    //     () => null
    //   );

    //   saveQuizState({
    //     topicId: "notes-practice",
    //     questions: quizPayload,
    //     goalDetails: { subject: "Notes Practice", current_level: 0 },
    //     currentQuestionIndex: 0,
    //     selectedOption: null,
    //     score: 0,
    //     showExplanation: false,
    //     quizCompleted: false,
    //     leveledUp: false,
    //     goalStatus: "",
    //     userAnswers: userAnswersSeed,
    //   });

    //   setSelectedAnswers(userAnswersSeed);
    //   setCurrentQuestionIndex(0);
    //   const computedTimer =
    //     typeof quizPayload?.timer === "number" && quizPayload.timer > 0
    //       ? quizPayload.timer
    //       : totalQuestions * 60;
    //   setRemainingTime(computedTimer);
    //   setIsModalOpen(true);
    //   clearAllUploadedFiles();
    // } catch (error) {
    //   console.error("Failed to start notes practice quiz:", error);
    //   const rateLimitMessage = deriveRateLimitMessageFromError(error);
    //   if (rateLimitMessage) {
    //     toast.warn(rateLimitMessage);
    //     showRateLimitModal(rateLimitMessage);
    //     return;
    //   }
    //   const fallbackQuestions = buildProvidedQuestions(selectedQuestions);
    //   if (fallbackQuestions.length) {
    //     const fallbackAnswers = Array.from(
    //       { length: fallbackQuestions.length },
    //       () => null
    //     );
    //     saveQuizState({
    //       topicId: "notes-practice",
    //       questions: fallbackQuestions,
    //       goalDetails: { subject: "Notes Practice", current_level: 0 },
    //       currentQuestionIndex: 0,
    //       selectedOption: null,
    //       score: 0,
    //       showExplanation: false,
    //       quizCompleted: false,
    //       leveledUp: false,
    //       goalStatus: "",
    //       userAnswers: fallbackAnswers,
    //     });
    //     setSelectedAnswers(fallbackAnswers);
    //     setCurrentQuestionIndex(0);
    //     setRemainingTime(
    //       fallbackQuestions.length * 60 || selectedQuestions * 60
    //     );
    //     setIsModalOpen(true);
    //     clearAllUploadedFiles();
    //   } else {
    //     toast.error("Could not generate a quiz.");
    //   }
    // } finally {
    //   setIsGeneratingQuiz(false);
    // }
    toast.success("Notes Practice coming soon!");
  };

  const handleOptionChange = (questionIndex, optionValue) => {
    setSelectedAnswers((prev) => {
      const updated = [...prev];
      updated[questionIndex] = optionValue;
      return updated;
    });
  };

  useEffect(() => {
    if (!isModalOpen) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isModalOpen]);

  useEffect(() => {
    // Reset content expansion when switching notes or opening modal
    setIsContentExpanded(false);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [selectedNote, showNotesModal]);

  const handlePrevQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNextQuestion = () => {
    setCurrentQuestionIndex((prev) =>
      Math.min(prev + 1, Math.max(selectedQuestions - 1, 0))
    );
  };

  const currentAnswer = selectedAnswers[currentQuestionIndex] || "";
  const questionNumber = currentQuestionIndex + 1;

  const selectedNoteSubject =
    selectedNote?.notes_extracted_content?.subject ||
    selectedNote?.subject ||
    "Notes";
  const selectedNoteTopic =
    selectedNote?.notes_extracted_content?.topic || selectedNote?.chapter || "";
  const selectedNoteSummary =
    selectedNote?.notes_extracted_content?.content_summary ||
    selectedNote?.content_summary ||
    "";
  const selectedNoteFiles = Array.isArray(selectedNote?.notes_data)
    ? selectedNote.notes_data
    : [];
  const selectedNoteResults = Array.isArray(selectedNote?.results)
    ? selectedNote.results
    : [];
  // Process source_versions data from selectedNote
  const selectedNoteSourceVersions = Array.isArray(
    selectedNote?.source_versions
  )
    ? selectedNote.source_versions
    : [];

  // Convert source_versions to attempt format for compatibility
  const selectedNoteAttempts = selectedNoteSourceVersions.map(
    (version, index) => ({
      attempt_id: `${selectedNote?.attempt_id || "unknown"}-v${
        version.source_version
      }`,
      id: `${selectedNote?.attempt_id || "unknown"}-v${version.source_version}`,
      attempted_date: version.attempted_date,
      date: version.attempted_date,
      marks_received: version.marks_received,
      total_score: version.total_score,
      totalMarks: version.total_score,
      overallMarks: version.marks_received,
      total_questions: version.total_questions,
      correct_answers: version.correct_answers,
      wrong_answers: version.wrong_answers || [],
      subject: selectedNote?.subject,
      chapter: selectedNote?.chapter,
      fileName:
        selectedNote?.notes_extracted_content?.topic || selectedNote?.chapter,
      // Calculate accuracy percentage
      accuracy:
        version.total_score > 0
          ? (version.marks_received / version.total_score) * 100
          : 0,
    })
  );

  const sortedSelectedNoteAttempts = [...selectedNoteAttempts].sort((a, b) => {
    const dateA = new Date(a?.attempted_date || a?.date || 0).getTime();
    const dateB = new Date(b?.attempted_date || b?.date || 0).getTime();
    return dateB - dateA;
  });
  const latestAttempt = sortedSelectedNoteAttempts[0] || null;
  const latestAttemptDateObj =
    latestAttempt && (latestAttempt?.attempted_date || latestAttempt?.date)
      ? new Date(latestAttempt?.attempted_date || latestAttempt?.date)
      : null;
  const latestAttemptDate =
    latestAttemptDateObj && !Number.isNaN(latestAttemptDateObj.getTime())
      ? latestAttemptDateObj.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : null;
  const latestAttemptMarks =
    latestAttempt &&
    (safeNumber(latestAttempt?.marks_received) ??
      safeNumber(latestAttempt?.overallMarks));
  const latestAttemptTotal =
    latestAttempt &&
    (safeNumber(latestAttempt?.total_score) ??
      safeNumber(latestAttempt?.totalMarks));
  const latestAttemptScoreDisplay =
    latestAttemptMarks !== null && latestAttemptMarks !== undefined
      ? `${latestAttemptMarks}${
          latestAttemptTotal !== null && latestAttemptTotal !== undefined
            ? `/${latestAttemptTotal}`
            : ""
        }`
      : null;
  const latestAttemptDisplay =
    latestAttemptDate && latestAttemptDate.length
      ? `${latestAttemptDate}${
          latestAttemptScoreDisplay
            ? ` - Score: ${latestAttemptScoreDisplay}`
            : ""
        }`
      : null;
  const primaryNoteFile = selectedNoteFiles[0] || null;
  const secondaryNoteFiles =
    selectedNoteFiles.length > 1 ? selectedNoteFiles.slice(1) : [];

  // Helper function to determine MIME type from base64 data
  const getMimeTypeFromBase64 = (base64Data, file) => {
    // Check if file has explicit type information from backend
    if (file.content_type) {
      return file.content_type;
    }
    if (file.type) {
      return file.type;
    }
    if (file.mimeType) {
      return file.mimeType;
    }

    // Try to determine from base64 header
    const header = base64Data.substring(0, 20);

    // PDF files start with specific base64 patterns
    if (base64Data.startsWith("JVBERi0") || base64Data.startsWith("JVBER")) {
      return "application/pdf";
    }

    // Image files
    if (header.includes("iVBORw0KGgo") || header.includes("iVBOR")) {
      return "image/png";
    }
    if (header.includes("/9j/") || header.includes("/9j")) {
      return "image/jpeg";
    }
    if (header.includes("R0lGOD") || header.includes("R0lG")) {
      return "image/gif";
    }
    if (header.includes("UklGR")) {
      return "image/webp";
    }

    // Default to binary if we can't determine
    return "application/octet-stream";
  };

  const decodeBase64String = (payload) => {
    if (typeof window !== "undefined" && typeof window.atob === "function") {
      return window.atob(payload);
    }
    if (typeof atob === "function") {
      return atob(payload);
    }
    if (typeof Buffer !== "undefined" && typeof Buffer.from === "function") {
      const buffer = Buffer.from(payload, "base64");
      return buffer.toString("binary");
    }
    return null;
  };

  const buildBlobUrlFromBase64 = (sanitizedBase64, mimeType) => {
    if (!sanitizedBase64) {
      return "";
    }

    const cachedUrl = base64ObjectUrlCacheRef.current.get(sanitizedBase64);
    if (cachedUrl) {
      return cachedUrl;
    }

    const canUseBlobUrls =
      typeof window !== "undefined" &&
      window.URL &&
      typeof window.URL.createObjectURL === "function" &&
      typeof Blob !== "undefined";

    if (!canUseBlobUrls) {
      const fallbackUrl = `data:${mimeType};base64,${sanitizedBase64}`;
      base64ObjectUrlCacheRef.current.set(sanitizedBase64, fallbackUrl);
      return fallbackUrl;
    }

    const decoded = decodeBase64String(sanitizedBase64);
    if (decoded === null) {
      const fallbackUrl = `data:${mimeType};base64,${sanitizedBase64}`;
      base64ObjectUrlCacheRef.current.set(sanitizedBase64, fallbackUrl);
      return fallbackUrl;
    }

    try {
      const byteArray = new Uint8Array(decoded.length);
      for (let index = 0; index < decoded.length; index += 1) {
        byteArray[index] = decoded.charCodeAt(index);
      }
      const blob = new Blob([byteArray], { type: mimeType });
      const objectUrl = window.URL.createObjectURL(blob);
      base64ObjectUrlCacheRef.current.set(sanitizedBase64, objectUrl);
      return objectUrl;
    } catch (error) {
      console.error("Unable to create Blob URL from base64:", error);
      const fallbackUrl = `data:${mimeType};base64,${sanitizedBase64}`;
      base64ObjectUrlCacheRef.current.set(sanitizedBase64, fallbackUrl);
      return fallbackUrl;
    }
  };

  const buildBase64FileUrl = (file) => {
    if (
      !file ||
      typeof file.base64_data !== "string" ||
      !/\S/.test(file.base64_data)
    ) {
      return "";
    }

    const trimmedPayload = file.base64_data.trim();
    const dataUrlMatch = trimmedPayload.match(/^data:([^;,]+);base64,(.*)$/i);
    const sanitizedBase64 = dataUrlMatch
      ? dataUrlMatch[2].replace(/\s+/g, "")
      : trimmedPayload.replace(/\s+/g, "");

    if (!sanitizedBase64.length) {
      return "";
    }

    const mimeFromHeader = dataUrlMatch?.[1] || "";
    const resolvedMime =
      mimeFromHeader ||
      file.content_type ||
      file.type ||
      file.mimeType ||
      getMimeTypeFromBase64(sanitizedBase64, file) ||
      "application/octet-stream";
    const loweredMime = resolvedMime.toLowerCase();

    if (loweredMime.includes("pdf")) {
      const objectUrl = buildBlobUrlFromBase64(sanitizedBase64, resolvedMime);
      console.log("Generated blob URL for base64 file:", {
        mimeType: resolvedMime,
        base64Length: sanitizedBase64.length,
      });
      return objectUrl;
    }

    const dataUrl = `data:${resolvedMime};base64,${sanitizedBase64}`;
    console.log("Generated data URL for base64 file:", {
      mimeType: resolvedMime,
      dataUrlLength: dataUrl.length,
    });
    return dataUrl;
  };

  const getFileUrl = (file) => {
    if (!file) return "";
    if (typeof file === "string") return file;
    if (typeof file.base64_data === "string" && /\S/.test(file.base64_data)) {
      const base64Url = buildBase64FileUrl(file);
      if (base64Url) {
        return base64Url;
      }
    }
    if (file && typeof file.path === "string" && file.path) {
      return file.path;
    }

    const rawOriginal =
      typeof file.originalPath === "string"
        ? file.originalPath.trim()
        : undefined;
    const rawPath =
      typeof file.path === "string" ? file.path.trim() : undefined;
    const staticPathPattern = /^\/(pdfs|images|assets|static|files)\//i;

    if (rawOriginal && staticPathPattern.test(rawOriginal)) {
      return rawOriginal;
    }

    if (rawPath && /^https?:/i.test(rawPath)) {
      return rawPath;
    }

    if (rawOriginal) {
      return rawOriginal;
    }

    if (rawPath) {
      return rawPath;
    }

    if (file.url) return file.url;
    if (file.location) return file.location;
    if (Array.isArray(file.alternatePaths)) {
      const fallback = file.alternatePaths.find(
        (candidate) => typeof candidate === "string" && candidate.length
      );
      if (fallback) {
        return fallback;
      }
    }

    return "";
  };

  const getFileName = (file, fallback = "Document") => {
    if (!file) return fallback;
    if (typeof file === "string") {
      const parts = file.split("/");
      return parts[parts.length - 1] || fallback;
    }

    // Handle base64 data files
    if (file.base64_data && typeof file.base64_data === "string") {
      if (file.name) return file.name;
      if (file.filename) return file.filename;

      // Generate filename based on file type
      const mimeType = getMimeTypeFromBase64(file.base64_data, file);
      const extension = mimeType.includes("pdf")
        ? ".pdf"
        : mimeType.includes("png")
        ? ".png"
        : mimeType.includes("jpeg") || mimeType.includes("jpg")
        ? ".jpg"
        : mimeType.includes("gif")
        ? ".gif"
        : mimeType.includes("webp")
        ? ".webp"
        : ".bin";

      return `document${extension}`;
    }

    if (file.name) return file.name;
    if (file.filename) return file.filename;
    const url = getFileUrl(file) || file.originalPath || "";
    if (typeof url === "string" && url.length) {
      const parts = url.split("/");
      return parts[parts.length - 1] || fallback;
    }
    return fallback;
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8 rounded-2xl relative">
      <h2 className="font-semibold text-gray-700 text-lg mb-4">
        Upload your notes & test your learning
      </h2>

      <div className="flex flex-col md:flex-row gap-4 md:gap-6 md:items-stretch min-h-[70vh] md:min-h-[80vh]">
        <aside className="order-2 md:order-1 w-full md:w-72 lg:w-80 border-2 border-[#0D99FF] bg-white rounded-xl shadow flex flex-col self-stretch max-h-[60vh] md:max-h-[80vh]">
          <div className="px-4 py-3 border-b border-[#0D99FF]/30">
            <h3 className="font-semibold text-[#0D99FF] text-sm uppercase tracking-wide">
              Notes History
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoadingNotes ? (
              <div className="h-full flex items-center justify-center px-4 py-6">
                <SimpleThreeDotsLoader message="Fetching notes" />
              </div>
            ) : oldNotes.length === 0 ? (
              <div className="h-full flex items-center justify-center px-4 py-6 text-center">
                <p className="text-sm text-gray-500">
                  No notes yet. Upload your first note to see it here.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-[#0D99FF]/20">
                {oldNotes.map((note, index) => {
                  const subject =
                    note?.notes_extracted_content?.subject ||
                    note?.subject ||
                    "Notes";
                  const topic =
                    note?.notes_extracted_content?.topic || note?.chapter || "";
                  const key =
                    note?.id ||
                    note?.note_id ||
                    `${subject.replace(/\s+/g, "-").toLowerCase()}-${index}`;

                  // Format date and time from attempted_date in local timezone
                  const formatDateTime = (dateString) => {
                    if (!dateString) return "";
                    try {
                      // Parse the date string as UTC (since API returns UTC without timezone info)
                      const date = new Date(dateString + "Z"); // Add 'Z' to indicate UTC
                      const now = new Date();
                      const diffInHours = (now - date) / (1000 * 60 * 60);

                      if (diffInHours < 24) {
                        // Show time if within 24 hours (local timezone)
                        return date.toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        });
                      } else if (diffInHours < 24 * 7) {
                        // Show date and day with time if within a week (local timezone)
                        const dateStr = date.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        });
                        const dayTimeStr = date.toLocaleDateString(undefined, {
                          weekday: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        });
                        return `${dateStr}, ${dayTimeStr}`;
                      } else {
                        // Show full date and time for older notes (local timezone)
                        return date.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        });
                      }
                    } catch (error) {
                      console.error("Error formatting date:", error);
                      return "";
                    }
                  };
                  const dateTime = formatDateTime(note?.generatedAt);

                  // Get status from note object - check multiple possible field names
                  const status =
                    note?.status ||
                    note?.note_status ||
                    note?.state ||
                    note?.note_state ||
                    note?.attempt_status ||
                    note?.completion_status ||
                    "complete";
                  const isActive = [
                    "active",
                    "in_progress",
                    "pending",
                    "ongoing",
                    "current",
                  ].includes(status?.toLowerCase());

                  // Status display component
                  const StatusBadge = ({ status }) => {
                    const statusConfig = {
                      active: {
                        text: "New Notes",
                        className:
                          "bg-green-100 text-green-800 border-green-200",
                        dotColor: "bg-green-500",
                      },
                      in_progress: {
                        text: "In Progress",
                        className: "bg-blue-100 text-blue-800 border-blue-200",
                        dotColor: "bg-blue-500",
                      },
                      pending: {
                        text: "Pending",
                        className:
                          "bg-yellow-100 text-yellow-800 border-yellow-200",
                        dotColor: "bg-yellow-500",
                      },
                      complete: {
                        text: "Completed",
                        className: "bg-gray-100 text-gray-600 border-gray-200",
                        dotColor: "bg-gray-400",
                      },
                    };

                    // Normalize status to lowercase for matching
                    const normalizedStatus = status?.toLowerCase();
                    const config =
                      statusConfig[normalizedStatus] || statusConfig.complete;

                    return (
                      <div
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.className}`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full mr-1.5 ${config.dotColor}`}
                        ></div>
                        {config.text}
                      </div>
                    );
                  };

                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => handleNoteClick(note)}
                        className={`w-full text-left px-4 py-3 text-gray-700 text-sm hover:bg-[#0D99FF]/10 transition-colors duration-200 flex items-center justify-between ${
                          isActive
                            ? "bg-green-50 border-l-4 border-green-400"
                            : ""
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className={`block font-medium ${
                                isActive ? "text-green-800" : "text-gray-800"
                              }`}
                            >
                              {subject}
                            </span>
                            <StatusBadge status={status} />
                          </div>
                          {topic && (
                            <div className="mb-1">
                              <span className="text-xs text-gray-500">
                                {topic}
                              </span>
                            </div>
                          )}
                          {dateTime && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-500">
                                {dateTime}
                              </span>
                            </div>
                          )}
                        </div>
                        <FaEye
                          className={`w-4 h-4 ml-2 flex-shrink-0 ${
                            isActive ? "text-green-500" : "text-gray-400"
                          }`}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* TODO: mobile responsive have camera, file etc. */}
        <div className="order-1 md:order-2 flex-1 flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <div className="bg-white rounded-2xl shadow">
              <div className="flex flex-wrap gap-4 border-b border-gray-200 px-4 sm:px-6 pt-4">
                {UPLOAD_TABS.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeUploadTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveUploadTab(tab.id)}
                      className={`flex items-center gap-2 pb-3 px-1 text-sm font-semibold border-b-2 transition-colors duration-200 -mb-px ${
                        isActive
                          ? "text-[#0D99FF] border-[#0D99FF]"
                          : "text-gray-400 border-transparent hover:text-gray-600"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 ${
                          isActive ? "text-[#0D99FF]" : "text-gray-400"
                        }`}
                      />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              <div className="p-4 sm:p-6">
                {activeUploadTab === "upload" ? (
                  <div className="flex flex-col gap-6">
                    {uploadedFiles.length === 0 ? (
                      <div
                        className={`border-2 border-dashed p-8 rounded-xl flex flex-col items-center justify-center text-center shadow min-h-[240px] cursor-pointer transition-all duration-200 ${
                          isDragOver
                            ? "border-[#0D99FF] bg-[#0D99FF] text-white"
                            : isDropZoneHovered
                            ? "border-[#0D99FF] bg-purple-50"
                            : "border-purple-300 bg-white hover:border-purple-400"
                        }`}
                        onDragOver={handleDragOver}
                        onDragLeave={(event) => {
                          handleDragLeave(event);
                          setIsDropZoneHovered(false);
                        }}
                        onDrop={(event) => {
                          handleDrop(event);
                          setIsDropZoneHovered(false);
                        }}
                        onMouseEnter={() => setIsDropZoneHovered(true)}
                        onMouseLeave={() => setIsDropZoneHovered(false)}
                        onClick={handleUpload}
                      >
                        <FaUpload
                          className={`w-12 h-12 mb-3 ${
                            isDragOver
                              ? "text-white"
                              : isDropZoneHovered
                              ? "text-[#0D99FF]"
                              : "text-[#0D99FF]"
                          }`}
                        />
                        <p
                          className={`text-lg font-semibold mb-1 ${
                            isDragOver
                              ? "text-white"
                              : isDropZoneHovered
                              ? "text-[#0D99FF]"
                              : "text-gray-800"
                          }`}
                        >
                          Drag & drop your files here
                        </p>
                        <p
                          className={`text-sm mt-5 ${
                            isDragOver ? "text-white/90" : "text-gray-500"
                          }`}
                        >
                          Upload your notes in the right page sequence for the
                          best results.
                        </p>
                        <p
                          className={`text-xs mt-2 ${
                            isDragOver ? "text-white/80" : "text-gray-500"
                          }`}
                        >
                          Supports PDF, JPG, or PNG formats. You can upload
                          multiple files.
                        </p>
                        <button
                          type="button"
                          className="bg-[#0D99FF] mt-5 hover:bg-purple-600 text-white font-semibold py-2 px-10 rounded-full shadow transition-colors duration-300"
                        >
                          {uploadedFiles.length > 0
                            ? "Add More Notes Files"
                            : "Upload Notes Files"}
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`border-2 rounded-lg p-6 bg-white shadow transition-colors ${
                          isDragOver
                            ? "border-[#0D99FF] bg-purple-50"
                            : "border-gray-300"
                        }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="flex items-center justify-between gap-4 mb-3">
                          <h3 className="text-md font-medium text-gray-800">
                            Uploaded Files
                          </h3>
                          <p className="text-xs text-gray-500">
                            Drag and drop more files here or use the button
                            below.
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-4">
                          {uploadedFiles.map((file, index) => {
                            const fileLabel = file?.name || `File ${index + 1}`;
                            const thumbnail = uploadedFileThumbnails[index];
                            const pdf = isPdfFile(file);
                            const thumbnailSrc =
                              thumbnail || "/images/answer_sheet_example.png";
                            return (
                              <div
                                key={`${fileLabel}-${index}`}
                                className="relative border border-gray-200 rounded-lg bg-white shadow-sm flex flex-col overflow-hidden flex-shrink-0 w-full sm:w-[48%] xl:w-[30%]"
                                style={{ maxWidth: "100%" }}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleUploadedFilePreview(file)
                                  }
                                  className="w-full h-32 bg-gray-50 flex items-center justify-center overflow-hidden cursor-pointer hover:bg-gray-100 transition-colors"
                                >
                                  <img
                                    src={thumbnailSrc}
                                    alt={
                                      pdf ? `${fileLabel} thumbnail` : fileLabel
                                    }
                                    className={`w-full h-full ${
                                      pdf ? "object-contain" : "object-cover"
                                    }`}
                                  />
                                  {pdf && (
                                    <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded">
                                      PDF
                                    </span>
                                  )}
                                </button>
                                <div className="px-3 py-2 flex items-center justify-between">
                                  <p
                                    className="text-xs text-gray-600 truncate pr-2"
                                    title={fileLabel}
                                  >
                                    {fileLabel}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeUploadedFile(index);
                                    }}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    title="Remove this file"
                                  >
                                    <FaTrash className="w-4 h-4 text-red-600 hover:text-red-700" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {uploadedFiles.length > 0 && (
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={handleUpload}
                          className="bg-[#0D99FF] hover:bg-purple-600 text-white font-semibold py-2 px-10 rounded-full shadow transition-colors duration-300"
                        >
                          Add More Notes Files
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-6">
                    {librarySelectedFiles.length === 0 ? (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowFileSelectionModal(true)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setShowFileSelectionModal(true);
                          }
                        }}
                        className="w-full border-2 border-dashed border-purple-200 rounded-xl p-8 text-center min-h-[240px] bg-gradient-to-b from-white to-purple-50 flex flex-col items-center justify-center gap-4 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0D99FF]"
                      >
                        <FaFolderOpen className="w-12 h-12 text-[#0D99FF]" />
                        <p className="text-lg font-semibold text-gray-800">
                          Select from your uploads
                        </p>
                        <p className="text-sm text-gray-500 max-w-md">
                          Browse the notes you've previously uploaded and
                          quickly start building a quiz.
                        </p>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setShowFileSelectionModal(true);
                          }}
                          className="bg-[#0D99FF] hover:bg-purple-600 text-white font-semibold py-2 px-10 rounded-full shadow transition-colors duration-300"
                        >
                          Select from My Uploads
                        </button>
                      </div>
                    ) : (
                      <div className=" border-2 border-dashed border-purple-200 rounded-xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0D99FF] p-6 bg-white shadow">
                        <div className="flex items-center justify-between gap-4 mb-3">
                          <h3 className="text-md font-medium text-gray-800">
                            Selected Files
                          </h3>
                          <p className="text-xs text-gray-500">
                            Use the button below to pick more notes.
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-4">
                          {librarySelectedFiles.map((file, index) => {
                            const fileLabel = file?.name || `File ${index + 1}`;
                            const thumbnail = librarySelectedThumbnails[index];
                            const pdf = isPdfFile(file);
                            const thumbnailSrc =
                              thumbnail || "/images/answer_sheet_example.png";
                            return (
                              <div
                                key={`${fileLabel}-${index}`}
                                className="relative border border-gray-200 rounded-lg bg-white shadow-sm flex flex-col overflow-hidden flex-shrink-0 w-full sm:w-[48%] xl:w-[30%]"
                                style={{ maxWidth: "100%" }}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleUploadedFilePreview(file)
                                  }
                                  className="w-full h-32 bg-gray-50 flex items-center justify-center overflow-hidden cursor-pointer hover:bg-gray-100 transition-colors"
                                >
                                  <img
                                    src={thumbnailSrc}
                                    alt={
                                      pdf ? `${fileLabel} thumbnail` : fileLabel
                                    }
                                    className={`w-full h-full ${
                                      pdf ? "object-contain" : "object-cover"
                                    }`}
                                  />
                                  {pdf && (
                                    <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded">
                                      PDF
                                    </span>
                                  )}
                                </button>
                                <div className="px-3 py-2 flex items-center justify-between">
                                  <p
                                    className="text-xs text-gray-600 truncate pr-2"
                                    title={fileLabel}
                                  >
                                    {fileLabel}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      removeLibraryFile(index);
                                    }}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    title="Remove this file"
                                  >
                                    <FaTrash className="w-4 h-4 text-red-600 hover:text-red-700" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {librarySelectedFiles.length > 0 && (
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={() => setShowFileSelectionModal(true)}
                          className="bg-[#0D99FF] hover:bg-purple-600 text-white font-semibold py-2 px-10 rounded-full shadow transition-colors duration-300"
                        >
                          Select from My Uploads
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChange}
            ref={fileInputRef}
            className="hidden"
            multiple
          />

          <div className="flex flex-col md:flex-row gap-4 md:gap-8 bg-white p-4 sm:p-6 rounded-xl shadow">
            <div className="flex-1">
              <h2 className="font-semibold text-gray-700 mb-2 text-sm sm:text-base">
                Maximum number of questions
              </h2>
              <div className="flex flex-wrap gap-2">
                {[5, 10, 15, 20].map((num) => (
                  <button
                    key={num}
                    onClick={() => setSelectedQuestions(num)}
                    className={`px-4 py-1.5 rounded-full border transition-colors duration-300 ${
                      selectedQuestions === num
                        ? "bg-[#0D99FF] text-white"
                        : "bg-white text-gray-600 border-gray-300"
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1">
              <h2 className="font-semibold text-gray-700 mb-2 text-sm sm:text-base">
                Select a Question Type
              </h2>
              <select
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                className="border border-gray-300 px-4 py-2 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-full max-w-xs"
              >
                <option value="MCQ">MCQ</option>
                <option value="Short Answer">Short Answer</option>
                <option value="All">All</option>
              </select>
            </div>
          </div>

          <div className="flex justify-center">
            {isGeneratingQuiz ? (
              <div className="flex flex-col items-center">
                <SimpleThreeDotsLoader message="Generating your quiz" />
              </div>
            ) : (
              <button
                onClick={submitHandler}
                className="bg-[#0D99FF] hover:bg-purple-600 text-white font-semibold py-2 px-10 rounded-full shadow transition-colors duration-300"
              >
                Create Practice Game
              </button>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            aria-hidden="true"
          />
          <div className="relative w-[98vw] h-[98vh] max-w-none rounded-2xl bg-white p-0 shadow-xl overflow-hidden">
            <div className="h-full">
              <GoalQuestions embedded onClose={closePracticeGame} />
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal - Full Screen */}
      {showNotesModal && selectedNote && (
        <MathJaxContext config={mathJaxConfig}>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white w-full h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b bg-[#0D99FF] text-white">
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    <MathJax inline dynamic>
                      {selectedNoteSubject || ""}
                    </MathJax>
                  </h2>
                  <p className="text-sm text-white opacity-90">
                    <MathJax inline dynamic>
                      {selectedNoteTopic || "Topic"}
                    </MathJax>
                  </p>
                </div>
                <button
                  onClick={() => setShowNotesModal(false)}
                  className="text-white hover:text-gray-200 transition-colors p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>

              {/* Content - Two Panel Layout */}
              <div className="flex-1 flex overflow-hidden">
                {/* Left Panel - Notes Files */}
                <div className="w-1/2 border-r border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Notes Files
                  </h3>
                  <div className="space-y-4 overflow-y-auto max-h-full">
                    {/* Primary file preview (notes_data[0]) */}
                    {selectedNoteFiles.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No files available for this note.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {isPdfFile(primaryNoteFile) ? (
                          <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-700 text-center">
                                {getFileName(primaryNoteFile)}
                              </p>
                            </div>
                            <div className="w-full h-[calc(100dvh-120px)] mb-4 border border-gray-300 rounded-lg overflow-hidden">
                              <iframe
                                src={getFileUrl(primaryNoteFile)}
                                className="w-full h-full"
                                title={getFileName(primaryNoteFile)}
                                type="application/pdf"
                                onError={(e) => {
                                  console.error("PDF failed to load:", e);
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                            <img
                              src={getFileUrl(primaryNoteFile)}
                              alt={getFileName(primaryNoteFile, "Primary file")}
                              className="w-full h-auto object-contain rounded-lg cursor-zoom-in hover:shadow-lg transition-shadow"
                              onClick={() =>
                                handleImagePreview(
                                  getFileUrl(primaryNoteFile),
                                  getFileName(primaryNoteFile, "Primary file")
                                )
                              }
                              onError={(e) => {
                                e.target.src =
                                  "/images/answer_sheet_example.png";
                              }}
                            />
                            <p className="text-sm text-gray-600 mt-2 text-center">
                              {getFileName(primaryNoteFile, "Primary file")}
                            </p>
                          </div>
                        )}

                        {secondaryNoteFiles.map((file, index) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
                          >
                            {file.type === "image" ? (
                              <div>
                                <img
                                  src={getFileUrl(file)}
                                  alt={getFileName(file)}
                                  className="w-full h-auto object-contain rounded-lg cursor-zoom-in hover:shadow-lg transition-shadow"
                                  onClick={() =>
                                    handleImagePreview(
                                      getFileUrl(file),
                                      getFileName(file)
                                    )
                                  }
                                  onError={(e) => {
                                    e.target.src =
                                      "/images/answer_sheet_example.png";
                                  }}
                                />
                                <p className="text-sm text-gray-600 mt-2 text-center">
                                  {getFileName(file)}
                                </p>
                              </div>
                            ) : isPdfFile(file) ? (
                              <div>
                                <div
                                  className="relative w-full h-48 bg-gray-100 rounded-lg cursor-pointer hover:shadow-lg transition-shadow flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-blue-400"
                                  onClick={() =>
                                    handlePdfPreview(
                                      getFileUrl(file),
                                      getFileName(file)
                                    )
                                  }
                                >
                                  <div className="text-center">
                                    <div className="w-16 h-16 mx-auto mb-3 bg-red-100 rounded-lg flex items-center justify-center">
                                      <svg
                                        className="w-8 h-8 text-red-600"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                    <p className="text-sm font-medium text-gray-700">
                                      PDF Document
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Click to preview
                                    </p>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-600 mt-2 text-center">
                                  {getFileName(file)}
                                </p>
                              </div>
                            ) : (
                              <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                                <p className="text-sm text-gray-500">
                                  Unsupported file type
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Panel - Metadata and Quiz History */}
                <div className="w-1/2 p-6 overflow-y-auto">
                  {isLoadingSelectedNote ? (
                    <div className="py-12 flex justify-center">
                      <SimpleThreeDotsLoader message="Loading note details" />
                    </div>
                  ) : (
                    <>
                      {/* Basic Metadata */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                          Note Information
                        </h3>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="space-y-3">
                            <div>
                              <span className="font-medium text-gray-700">
                                Subject:
                              </span>
                              <LatexText
                                component="span"
                                inline
                                className="ml-2 text-gray-600"
                                text={selectedNoteSubject}
                              />
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">
                                Topic:
                              </span>
                              <LatexText
                                component="span"
                                inline
                                className="ml-2 text-gray-600"
                                text={selectedNoteTopic || "Topic"}
                              />
                            </div>
                            {latestAttemptDate && (
                              <div>
                                <span className="font-medium text-gray-700">
                                  Last Attempt:
                                </span>
                                <LatexText
                                  component="span"
                                  inline
                                  className="ml-2 text-gray-600"
                                  text={latestAttemptDisplay}
                                />
                              </div>
                            )}
                            {/* <div>
                          <span className="font-medium text-gray-700">
                            Content Summary:
                          </span>
                          <LatexText
                            component="p"
                            className={`mt-1 text-sm text-gray-600 leading-relaxed ${
                              isContentExpanded ? "" : "line-clamp-2"
                            }`}
                            text={selectedNoteSummary}
                          />
                          {selectedNoteSummary &&
                            selectedNoteSummary.length > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setIsContentExpanded((prev) => !prev)
                                }
                                className="mt-2 text-[#0D99FF] hover:underline text-sm font-medium"
                              >
                                {isContentExpanded ? "Show less" : "Read more"}
                              </button>
                            )}
                        </div> */}
                          </div>
                        </div>
                      </div>

                      {/* Accuracy Trend Chart */}
                      {sortedSelectedNoteAttempts.length > 0 && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            Accuracy Trend
                          </h3>
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="h-64 w-full">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                  data={processAttemptsForChart(
                                    sortedSelectedNoteAttempts
                                  )}
                                >
                                  <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#f0f0f0"
                                  />
                                  <XAxis
                                    dataKey="attempt"
                                    tick={{ fontSize: 12 }}
                                    stroke="#666"
                                  />
                                  <YAxis
                                    domain={[0, 100]}
                                    tick={{ fontSize: 12 }}
                                    stroke="#666"
                                    label={{
                                      value: "Accuracy (%)",
                                      angle: -90,
                                      position: "insideLeft",
                                    }}
                                  />
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: "#fff",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "8px",
                                      boxShadow:
                                        "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                    }}
                                    formatter={(value, name, props) => {
                                      if (name === "accuracy") {
                                        return [`${value}%`, "Accuracy"];
                                      }
                                      return [value, name];
                                    }}
                                    labelFormatter={(label, payload) => {
                                      if (payload && payload.length > 0) {
                                        const data = payload[0].payload;
                                        return `Attempt: ${label} | Score: ${data.score}/${data.maxMark}`;
                                      }
                                      return `Attempt: ${label}`;
                                    }}
                                  />
                                  <Legend />
                                  <Line
                                    type="monotone"
                                    dataKey="accuracy"
                                    stroke="#0D99FF"
                                    strokeWidth={3}
                                    dot={{
                                      fill: "#0D99FF",
                                      strokeWidth: 2,
                                      r: 6,
                                    }}
                                    activeDot={{
                                      r: 8,
                                      stroke: "#0D99FF",
                                      strokeWidth: 2,
                                    }}
                                    name="Accuracy %"
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Quiz History */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                          Previous Quiz Attempts
                        </h3>

                        {/* Summary Statistics */}
                        {sortedSelectedNoteAttempts.length > 0 && (
                          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-medium text-blue-800 mb-2">
                              Overall Performance
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-blue-700">
                                  Total Attempts:
                                </span>
                                <p className="text-blue-600">
                                  {sortedSelectedNoteAttempts.length}
                                </p>
                              </div>
                              <div>
                                <span className="font-medium text-blue-700">
                                  Average Score:
                                </span>
                                <p className="text-blue-600">
                                  {sortedSelectedNoteAttempts.length > 0
                                    ? Math.round(
                                        (sortedSelectedNoteAttempts.reduce(
                                          (sum, attempt) =>
                                            sum + (attempt.accuracy || 0),
                                          0
                                        ) /
                                          sortedSelectedNoteAttempts.length) *
                                          100
                                      ) / 100
                                    : 0}
                                  %
                                </p>
                              </div>
                              <div>
                                <span className="font-medium text-blue-700">
                                  Best Score:
                                </span>
                                <p className="text-blue-600">
                                  {Math.max(
                                    ...sortedSelectedNoteAttempts.map(
                                      (attempt) => attempt.accuracy || 0
                                    )
                                  )}
                                  %
                                </p>
                              </div>
                              <div>
                                <span className="font-medium text-blue-700">
                                  Latest Attempt:
                                </span>
                                <p className="text-blue-600">
                                  {sortedSelectedNoteAttempts[0]?.accuracy || 0}
                                  %
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="space-y-4">
                          {sortedSelectedNoteAttempts.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              No previous attempts found for this note yet.
                            </p>
                          ) : (
                            sortedSelectedNoteAttempts.map(
                              (attempt, attemptIndex) => {
                                const attemptId =
                                  attempt?.attempt_id ||
                                  attempt?.id ||
                                  `attempt-${attemptIndex}`;
                                const isExpanded =
                                  expandedQuizzes.has(attemptId);
                                const attemptDateSource =
                                  attempt?.date || attempt?.attempted_date;
                                const attemptDate = attemptDateSource
                                  ? new Date(
                                      attemptDateSource
                                    ).toLocaleDateString(undefined, {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : "Unknown date";
                                const resolvedMarksReceived = safeNumber(
                                  attempt?.marks_received
                                );
                                const resolvedTotalScore = safeNumber(
                                  attempt?.total_score
                                );
                                const marksReceivedRaw =
                                  resolvedMarksReceived ??
                                  safeNumber(attempt?.overallMarks) ??
                                  0;
                                const totalScoreRaw =
                                  resolvedTotalScore ??
                                  safeNumber(attempt?.totalMarks) ??
                                  null;
                                const marksReceived =
                                  marksReceivedRaw >= 0 ? marksReceivedRaw : 0;
                                const totalScore =
                                  totalScoreRaw !== null && totalScoreRaw >= 0
                                    ? totalScoreRaw
                                    : null;
                                const wrongAnswers = Array.isArray(
                                  attempt?.wrong_answers
                                )
                                  ? attempt.wrong_answers
                                  : [];
                                const attemptQuestions = Array.isArray(
                                  attempt?.questions
                                )
                                  ? attempt.questions
                                  : [];
                                const hasQuestions =
                                  attemptQuestions.length > 0;
                                const hasWrongAnswers = wrongAnswers.length > 0;
                                const attemptTitle =
                                  attempt?.fileName ||
                                  attempt?.chapter ||
                                  attempt?.subject ||
                                  `Attempt ${attemptIndex + 1}`;
                                return (
                                  <div
                                    key={attemptId}
                                    className="border-2 border-blue-200 rounded-lg bg-white"
                                  >
                                    {/* Quiz Header - Clickable */}
                                    <div
                                      className="flex items-center justify-between p-3 bg-white border-b border-blue-200 rounded-t-lg cursor-pointer hover:bg-blue-50 transition-colors"
                                      onClick={() => toggleQuiz(attemptId)}
                                    >
                                      <div className="flex items-center gap-3">
                                        {isExpanded ? (
                                          <FaChevronDown className="w-4 h-4 text-blue-600" />
                                        ) : (
                                          <FaChevronRight className="w-4 h-4 text-blue-600" />
                                        )}
                                        <div>
                                          <LatexText
                                            component="h4"
                                            inline
                                            className="text-md font-medium text-blue-800"
                                            text={attemptTitle}
                                          />
                                          <p className="text-sm text-blue-600">
                                            <MathJax inline dynamic>
                                              {attemptDate}
                                            </MathJax>
                                          </p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <span className="text-lg font-bold text-blue-600">
                                          {marksReceived}
                                          {totalScore !== null
                                            ? `/${totalScore}`
                                            : ""}
                                        </span>
                                        <p className="text-xs text-blue-500">
                                          Score ({attempt?.correct_answers || 0}
                                          /{attempt?.total_questions || 0}{" "}
                                          correct)
                                        </p>
                                      </div>
                                    </div>

                                    {/* Collapsible Content */}
                                    {isExpanded && (
                                      <div className="p-4 border-t border-blue-100">
                                        <h5 className="font-medium text-gray-800 mb-3">
                                          {hasQuestions
                                            ? "Questions & Feedback"
                                            : "Wrong Questions & Corrections"}
                                        </h5>
                                        <div className="space-y-3">
                                          {hasQuestions &&
                                            attemptQuestions.map(
                                              (question, questionIndex) => {
                                                const questionMarks =
                                                  safeNumber(question?.marks);
                                                const questionTotalMarks =
                                                  safeNumber(
                                                    question?.totalMarks
                                                  );
                                                return (
                                                  <div
                                                    key={
                                                      question?.id ||
                                                      questionIndex
                                                    }
                                                    className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                                                  >
                                                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                                      <p className="font-medium text-gray-800">
                                                        Question{" "}
                                                        {questionIndex + 1}
                                                      </p>
                                                      <span className="text-sm font-semibold text-blue-600">
                                                        {questionMarks ?? 0}
                                                        {questionTotalMarks !==
                                                        null
                                                          ? `/${questionTotalMarks}`
                                                          : ""}
                                                      </span>
                                                    </div>
                                                    {question?.question_text && (
                                                      <LatexText
                                                        component="p"
                                                        className="text-sm text-gray-700 mt-1 whitespace-pre-wrap"
                                                        text={
                                                          question.question_text
                                                        }
                                                      />
                                                    )}
                                                    {question?.answer_text && (
                                                      <div className="mt-3">
                                                        <span className="font-medium text-gray-700">
                                                          Your Answer:
                                                        </span>
                                                        <LatexText
                                                          component="p"
                                                          className="text-sm text-gray-600 mt-1 whitespace-pre-wrap"
                                                          text={
                                                            question.answer_text
                                                          }
                                                        />
                                                      </div>
                                                    )}
                                                    {question?.feedback && (
                                                      <div className="mt-3">
                                                        <span className="font-medium text-gray-700">
                                                          Feedback:
                                                        </span>
                                                        <LatexText
                                                          component="p"
                                                          className="text-sm text-gray-600 mt-1 whitespace-pre-wrap"
                                                          text={
                                                            question.feedback
                                                          }
                                                        />
                                                      </div>
                                                    )}
                                                    {Array.isArray(
                                                      question?.stepBreakdown
                                                    ) &&
                                                      question.stepBreakdown
                                                        .length > 0 && (
                                                        <div className="mt-3 space-y-2">
                                                          <p className="font-medium text-gray-800">
                                                            Step Breakdown
                                                          </p>
                                                          {question.stepBreakdown.map(
                                                            (
                                                              step,
                                                              stepIndex
                                                            ) => {
                                                              const stepMarks =
                                                                safeNumber(
                                                                  step?.marks
                                                                );
                                                              const stepMaxMarks =
                                                                safeNumber(
                                                                  step?.maxMarks
                                                                );
                                                              const stepNumber =
                                                                safeNumber(
                                                                  step?.step
                                                                ) ??
                                                                stepIndex + 1;
                                                              return (
                                                                <div
                                                                  key={`${questionIndex}-${stepIndex}`}
                                                                  className="bg-white border border-gray-200 rounded-md p-2"
                                                                >
                                                                  <div className="flex items-center justify-between text-sm font-medium text-gray-700">
                                                                    <span>
                                                                      Step{" "}
                                                                      {
                                                                        stepNumber
                                                                      }
                                                                    </span>
                                                                    <span className="text-blue-600">
                                                                      {stepMarks ??
                                                                        0}
                                                                      {stepMaxMarks !==
                                                                      null
                                                                        ? `/${stepMaxMarks}`
                                                                        : ""}
                                                                    </span>
                                                                  </div>
                                                                  {step?.feedback && (
                                                                    <LatexText
                                                                      component="p"
                                                                      className="text-sm text-gray-600 mt-1 whitespace-pre-wrap"
                                                                      text={
                                                                        step.feedback
                                                                      }
                                                                    />
                                                                  )}
                                                                </div>
                                                              );
                                                            }
                                                          )}
                                                        </div>
                                                      )}
                                                  </div>
                                                );
                                              }
                                            )}
                                          {!hasQuestions &&
                                            hasWrongAnswers &&
                                            wrongAnswers.map(
                                              (wrong, wrongIndex) => {
                                                const questionText =
                                                  wrong?.question_text ||
                                                  "Question unavailable";
                                                return (
                                                  <div
                                                    key={wrongIndex}
                                                    className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                                                  >
                                                    <p className="font-medium text-gray-800 mb-2">
                                                      Question:{" "}
                                                      <MathJax inline dynamic>
                                                        {questionText}
                                                      </MathJax>
                                                    </p>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                                      <div>
                                                        <span className="font-medium text-red-600">
                                                          Your Answer:
                                                        </span>
                                                        <LatexText
                                                          component="p"
                                                          className="text-red-600"
                                                          text={
                                                            wrong?.selected_answer_text ||
                                                            "—"
                                                          }
                                                        />
                                                      </div>
                                                      <div>
                                                        <span className="font-medium text-green-600">
                                                          Correct Answer:
                                                        </span>
                                                        <LatexText
                                                          component="p"
                                                          className="text-green-600"
                                                          text={
                                                            wrong?.correct_answer_text ||
                                                            "—"
                                                          }
                                                        />
                                                      </div>
                                                    </div>
                                                    <div className="mt-2">
                                                      <span className="font-medium text-gray-700">
                                                        Explanation:
                                                      </span>
                                                      <LatexText
                                                        component="p"
                                                        className="text-sm text-gray-600 mt-1"
                                                        text={
                                                          wrong?.reasoning ||
                                                          "No reasoning provided."
                                                        }
                                                      />
                                                    </div>
                                                  </div>
                                                );
                                              }
                                            )}
                                          {!hasQuestions &&
                                            !hasWrongAnswers && (
                                              <p className="text-sm text-gray-500">
                                                No detailed feedback available
                                                for this attempt yet.
                                              </p>
                                            )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                            )
                          )}
                        </div>
                      </div>

                      {/* Give New Quiz Button */}
                      {isGeneratingQuiz ? (
                        <div className="flex justify-center flex-col items-center">
                          <SimpleThreeDotsLoader message="Generating your quiz" />
                        </div>
                      ) : (
                        <div className="flex justify-center">
                          <button
                            onClick={handleNewQuiz}
                            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                          >
                            <FaEye className="w-5 h-5" />
                            Give New Quiz
                          </button>
                          <div className="flex justify-center"></div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </MathJaxContext>
      )}

      {/* Image Preview Modal */}
      {showImagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-[#0D99FF] text-white rounded-t-lg">
              <h3 className="text-lg font-semibold">{previewImageAlt}</h3>
              <button
                onClick={() => setShowImagePreview(false)}
                className="text-white hover:text-gray-200 transition-colors p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            {/* Image Content */}
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              <img
                src={previewImage}
                alt={previewImageAlt}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
                onError={(e) => {
                  e.target.src = "/images/answer_sheet_example.png";
                }}
              />
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-[#0D99FF] text-white rounded-b-lg">
              <div className="flex justify-between items-center">
                <p className="text-sm text-white opacity-90">
                  Click outside to close
                </p>
                <button
                  onClick={() => setShowImagePreview(false)}
                  className="px-4 py-2 bg-white text-[#0D99FF] rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {showPdfPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl max-h-[95vh] flex flex-col shadow-2xl w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-[#0D99FF] text-white rounded-t-lg">
              <h3 className="text-lg font-semibold">{previewImageAlt}</h3>
              <button
                onClick={() => setShowPdfPreview(false)}
                className="text-white hover:text-gray-200 transition-colors p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            {/* PDF Content */}
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              <iframe
                src={previewPdf}
                className="w-full h-full min-h-[70vh] border-0 rounded-lg shadow-lg"
                title={previewImageAlt}
                type="application/pdf"
                onError={(e) => {
                  console.error("PDF failed to load:", e);
                }}
              />
            </div>

            {/* Footer */}
            {/* <div className="p-4 border-t bg-[#0D99FF] text-white rounded-b-lg">
              <div className="flex justify-between items-center">

                <div className="flex gap-2">
                  <a
                    href={previewPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-white text-[#0D99FF] rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  >
                    Open in New Tab
                  </a>
                  <button
                    onClick={() => setShowPdfPreview(false)}
                    className="px-4 py-2 bg-white text-[#0D99FF] rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      )}

      {/* File Selection Modal */}
      {showFileSelectionModal && (
        <div className="fixed inset-0 bg-gradient-to-br from-[#0D99FF]/20 to-[#9F7AEA]/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6">
              <h2 className="text-xl font-semibold text-gray-800">
                Select from My Uploads to Practice
              </h2>
              <button
                onClick={() => setShowFileSelectionModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            {/* Filter Section */}
            <div className="p-6 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-2">
                  <FaFilter className="w-4 h-4 text-gray-500" />
                  <select
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={uploadFilter.subject}
                    onChange={(e) => {
                      const nextSubject = e.target.value;
                      setUploadFilter((prev) => ({
                        subject: nextSubject,
                        chapter:
                          nextSubject && nextSubject === prev.subject
                            ? prev.chapter
                            : "",
                      }));
                      setCurrMyUploadsPage(1);
                    }}
                  >
                    <option value="">All Subjects</option>
                    {subjects &&
                    Array.isArray(subjects) &&
                    subjects.length > 0 ? (
                      subjects.map((subject) => (
                        <option key={subject} value={subject}>
                          {subject}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="Mathematics">Mathematics</option>
                        <option value="Physics">Physics</option>
                        <option value="Chemistry">Chemistry</option>
                        <option value="Biology">Biology</option>
                      </>
                    )}
                  </select>
                </div>
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={uploadFilter.chapter}
                  onChange={(e) => {
                    const nextChapter = e.target.value;
                    setUploadFilter((prev) => ({
                      ...prev,
                      chapter: nextChapter,
                    }));
                    setCurrMyUploadsPage(1);
                  }}
                >
                  <option value="">All Chapters</option>
                  {getFilteredChapters().map((chapter) => (
                    <option key={chapter} value={chapter}>
                      {chapter}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* File Details Section */}
            <div className="flex-1 overflow-auto p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
                FILE DETAILS
              </h3>

              <div className="space-y-4">
                {isFetchingWorkbooks ? (
                  <div className="py-12 flex justify-center">
                    <SimpleThreeDotsLoader message="Loading your workbooks" />
                  </div>
                ) : userWorkbooks.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    No workbooks found for the selected filters.
                  </div>
                ) : (
                  userWorkbooks.map((workbook, index) => {
                    const workbookId =
                      workbook?.id ||
                      workbook?.requestId ||
                      workbook?.workbookId ||
                      workbook?.path ||
                      `workbook-${index}`;
                    const rawFileName =
                      workbook?.filename ||
                      workbook?.fileName ||
                      workbook?.name ||
                      `Workbook ${index + 1}`;
                    const fileSizeText = formatFileSize(workbook?.size);
                    const subjectText = workbook?.subject;
                    const chapterText = workbook?.chapter;
                    const isImporting = isImportingWorkbookId === workbookId;
                    const displayedName = truncateFileName(rawFileName, 40);
                    const uploadDateText = formatUploadDate(workbook);
                    const uploadTimeText = formatUploadTime(workbook);

                    return (
                      <div
                        key={workbookId}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-4 border-b border-gray-100"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
                            <span className="text-gray-500 text-xs">
                              {getFileExtensionLabel(workbook)}
                            </span>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {displayedName}
                            </div>
                            {fileSizeText && (
                              <div className="text-sm text-gray-500">
                                {fileSizeText}
                              </div>
                            )}
                            {(subjectText || chapterText) && (
                              <div className="flex flex-wrap gap-2 mt-1">
                                {subjectText && (
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                                    {subjectText}
                                  </span>
                                )}
                                {chapterText && (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                    {chapterText}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto">
                          <div className="text-right text-sm text-gray-500 flex-shrink-0">
                            <div>{uploadDateText || "--"}</div>
                            {uploadTimeText && <div>{uploadTimeText}</div>}
                          </div>
                          <button
                            onClick={() => openUploadedSheet(workbook)}
                            disabled={isImporting}
                            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                              isImporting
                                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                                : "bg-[#0D99FF] text-white hover:bg-purple-600"
                            }`}
                          >
                            {isImporting ? "Adding..." : "Select"}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Pagination Footer */}
            <div className="p-6 border-t bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="text-sm text-gray-500">
                  {getWorkbookRangeText()}
                </div>
                {totalWorkbookPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setCurrMyUploadsPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currMyUploadsPage === 1}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-lg">&lt;</span>
                    </button>
                    {Array.from(
                      { length: Math.min(totalWorkbookPages, 5) },
                      (_, index) => {
                        let pageNumber;
                        if (totalWorkbookPages <= 5) {
                          pageNumber = index + 1;
                        } else if (currMyUploadsPage <= 3) {
                          pageNumber = index + 1;
                        } else if (
                          currMyUploadsPage >=
                          totalWorkbookPages - 2
                        ) {
                          pageNumber = totalWorkbookPages - 4 + index;
                        } else {
                          pageNumber = currMyUploadsPage - 2 + index;
                        }

                        pageNumber = Math.max(
                          1,
                          Math.min(pageNumber, totalWorkbookPages)
                        );

                        return (
                          <button
                            key={`${pageNumber}-${index}`}
                            onClick={() => setCurrMyUploadsPage(pageNumber)}
                            className={`px-3 py-1 text-sm rounded ${
                              currMyUploadsPage === pageNumber
                                ? "bg-[#0D99FF] text-white"
                                : "text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      }
                    )}
                    <button
                      onClick={() =>
                        setCurrMyUploadsPage((prev) =>
                          Math.min(prev + 1, totalWorkbookPages)
                        )
                      }
                      disabled={currMyUploadsPage === totalWorkbookPages}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-lg">&gt;</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <RateLimitModal
        isOpen={isRateLimitModalOpen}
        serverMessage={rateLimitServerMessage}
        supportMessage="You've reached the current limit for Notes Practice. Please wait a bit before trying again or contact support to increase your allowance."
        onClose={handleRateLimitModalClose}
      />

      <Outlet />
    </div>
  );
};

export default NotesPractice;
